import React, { useState, useEffect, useRef } from 'react';
import AddCustomerModal from '../../components/Modals/addCustomer';
import Select from 'react-select';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CardComponent from '../../components/cards/CardComponent';
import { Textfield, TextfieldwithLabel } from '../../components/Buttons/Textfield';
import getAmountInWords from '../../components/numbertoWords';
import fetchData from '../../functions/fetchData';
import { getHeaders } from '../../utility/getHeader';
import { GSTInvoicePrintPreview } from '../../components/Templates/gstTemplates';
import { VATInvoicePrintPreview } from '../../components/Templates/vatemplate';
import { VAT2InvoicePrintPreview } from '../../components/Templates/vattemplate2';
import customerDisplayManager from '../../services/CustomerDisplayManager';
import { getUserName } from '../../functions/storageUtils';
import { getNextSetupDate } from '../../utils/setupDateUtils';

// Token expiration check function
const checkTokenExpiration = (navigate) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (!token) {
    handleAutoLogout(navigate);
    return false;
  }
  
  try {
    // Decode JWT token to check expiration
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    
    if (payload.exp && payload.exp < currentTime) {
      handleAutoLogout(navigate);
      return false;
    }
    return true;
  } catch (error) {
    // If token is not JWT or invalid, logout
    handleAutoLogout(navigate);
    return false;
  }
};

// Auto logout function
const handleAutoLogout = (navigate) => {
  localStorage.removeItem('token');
  localStorage.removeItem('usertype');
  localStorage.removeItem('username');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('usertype');
  sessionStorage.removeItem('username');
  toast.error('Session expired. Please login again.');
  navigate('/');
};

// Setup axios interceptor for global error handling
const setupAxiosInterceptors = (navigate) => {
  // Response interceptor to catch 401/403 errors
  const interceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        handleAutoLogout(navigate);
      }
      return Promise.reject(error);
    }
  );
  
  // Return cleanup function
  return () => {
    axios.interceptors.response.eject(interceptorId);
  };
};

// Utility to fetch coresetting tax_type
async function fetchTaxType() {
  try {
    // coresetting table is usually a single row
    const res = await fetchData('coresetting', null, 'id', {});
    if (Array.isArray(res) && res.length > 0) {
      return res[0].tax_type ? res[0].tax_type.toLowerCase() : 'gst';
    }
    return 'gst'; // default fallback
  } catch (e) {
    return 'gst';
  }
}

export default function Sale() {
  const itemNameRefs = useRef([])
  const [cart] = useState([])
  const [barcode, setBarcode] = useState('')
  const [total, setTotal] = useState(0)
  const [paymentType, setPaymentType] = useState('Cash')
  const navigate = useNavigate();
  
  // Setup token expiration checking and axios interceptors
  useEffect(() => {
    // Check token on component mount
    if (!checkTokenExpiration(navigate)) {
      return; // Component will unmount due to navigation
    }
    
    // Setup axios interceptors for this component
    const cleanupInterceptors = setupAxiosInterceptors(navigate);
    
    // Set up periodic token checking (every 5 minutes)
    const tokenCheckInterval = setInterval(() => {
      if (!checkTokenExpiration(navigate)) {
        clearInterval(tokenCheckInterval);
      }
    }, 5 * 60 * 1000); // 5 minutes
    
    // Cleanup function
    return () => {
      cleanupInterceptors();
      clearInterval(tokenCheckInterval);
    };
  }, [navigate]);
  
  // Check if user is cashier
  const userType = localStorage.getItem('usertype') || sessionStorage.getItem('usertype');
  const isCashier = userType === 'Cashier';
  
  // Navigate to dashboard function
  const navigateToDashboard = () => {
    if (userType === 'Admin') {
      navigate('/dashboard'); // Analytics dashboard for admin
    } else if (userType === 'Cashier') {
      navigate('/CashierDashboard'); // Cashier dashboard
    } else {
      navigate('/dashboard'); // Default dashboard
    }
  };

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('usertype');
    localStorage.removeItem('username');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('usertype');
    sessionStorage.removeItem('username');
    navigate('/login');
  };
  // Tax type state
  const [taxType, setTaxType] = useState('gst');
  // On mount, fetch tax type from coresetting
  useEffect(() => {
    (async () => {
      const ttype = await fetchTaxType();
      setTaxType(ttype);
    })();
  }, []);
  const [discountType, setDiscountType] = useState('percentage')
  const [discountValue, setDiscountValue] = useState(0)
  const [bankAccountId, setBankAccountId] = useState(null)
  const [referenceNumber, setReferenceNumber] = useState('')
  const [remarks, setRemarks] = useState('')
  const [bankAccounts, setBankAccounts] = useState([])
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [showCustomerDetails, setShowCustomerDetails] = useState(true) // New state for Customer Details visibility
  const [showScanCode, setShowScanCode] = useState(true) // New state for Scan Code section visibility
  const [gstInvoiceProps, setGstInvoiceProps] = useState(null);
  const [vatInvoiceProps, setVatInvoiceProps] = useState(null);
  const [showVatDetails, setShowVatDetails] = useState(true); // State for showing/hiding VAT details in invoice
  const [reprintInvoiceNo, setReprintInvoiceNo] = useState(''); // State for reprint invoice number
  const [isEditMode, setIsEditMode] = useState(false); // State for edit mode
  const [editBillId, setEditBillId] = useState(null); // State for bill ID being edited
  const [editInvoiceNo, setEditInvoiceNo] = useState(''); // State for edit invoice search
  const [showPasswordModal, setShowPasswordModal] = useState(false); // State for password modal
  const [editPassword, setEditPassword] = useState(''); // State for edit password input
  const [pendingEditInvoiceNo, setPendingEditInvoiceNo] = useState(''); // State for pending invoice to edit

  
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    phone: ''
  })
  const [showModal, setShowModal] = useState(false)
  const [modalInvoiceData, setModalInvoiceData] = useState({
    companyDetails: {},
    customerDetails: {},
    data: [],
    bill_id: ''
  })
  const [itemRows, setItemRows] = useState([])
  const [isCustomerDisplayOpen, setIsCustomerDisplayOpen] = useState(false)
  const companyDetails = {
    name: 'Veloura Pvt. Ltd.',
    address: '123 Beach Road, Pattaya, Thailand',
    phone: '+66 987 654 321',
    email: 'info@veloura.com'
  }


  const [editableRowIndex, setEditableRowIndex] = useState(null)
  const [customer, setCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gst: '',
    deliveryPlace: ''
  })
  const [invoiceSummary, setInvoiceSummary] = useState({
    itemTotal: 0,
    discount: 0,
    subTotal: 0,
    tax: 0,
    roundOff: 0,
    grandTotal: 0
  })
  // Update invoice summary calculation
useEffect(() => {
  let itemTotal = 0;
  let discount = 0;
  let taxValue = 0;
  let taxPercent = 0;
  let subTotal = 0;
  let roundOff = 0;
  let grandTotal = 0;
  let taxTypeLabel = taxType === 'vat' ? 'VAT' : 'GST';
  let taxPercentDisplay = '';

  if (itemRows.length > 0) {
    // For tax-included items, itemTotal should be the base amount (before tax)
    // For tax-excluded items, itemTotal should be the amount (before tax is added)
    itemTotal = itemRows.reduce((sum, r) => sum + r.amount, 0);
    discount = itemRows.reduce((sum, r) => sum + r.discountValue, 0);
    subTotal = itemTotal - discount;
    // Calculate tax percent and value
    if (taxType === 'vat') {
      // VAT: use first nonzero VAT% as display
      const vatRows = itemRows.filter(r => r.vat > 0);
      taxPercent = vatRows.length > 0 ? vatRows[0].vat : 0;
      // If any item is taxIncluded, extract tax from netAmount, else add tax on top
      const hasIncludedTax = vatRows.some(r => r.taxIncluded);
      if (hasIncludedTax) {
        // Tax included: extract tax from netAmount (which is amount - discount)
        taxValue = itemRows.reduce((sum, r) => {
          if (r.taxIncluded && r.vat > 0) {
            // Extract VAT from net amount: VAT = netAmount - (netAmount / (1 + vat%))
            return sum + (r.netAmount - (r.netAmount / (1 + (r.vat / 100))));
          }
          return sum;
        }, 0);
      } else {
        // Tax excluded: add tax on top
        taxValue = itemRows.reduce((sum, r) => {
          if (!r.taxIncluded && r.vat > 0) {
            return sum + ((r.amount - r.discountValue) * (r.vat / 100));
          }
          return sum;
        }, 0);
      }
      taxPercentDisplay = `VAT (${taxPercent}%)`;
    } else {
      // GST: sum all GST fields
      let cgst = 0, sgst = 0, igst = 0;
      let cgstVal = 0, sgstVal = 0, igstVal = 0;
      itemRows.forEach(r => {
        if (r.cgst > 0) {
          cgst = r.cgst;
          cgstVal += r.taxIncluded ? (r.netAmount - (r.netAmount / (1 + (r.cgst / 100)))) : ((r.amount - r.discountValue) * (r.cgst / 100));
        }
        if (r.sgst > 0) {
          sgst = r.sgst;
          sgstVal += r.taxIncluded ? (r.netAmount - (r.netAmount / (1 + (r.sgst / 100)))) : ((r.amount - r.discountValue) * (r.sgst / 100));
        }
        if (r.igst > 0) {
          igst = r.igst;
          igstVal += r.taxIncluded ? (r.netAmount - (r.netAmount / (1 + (r.igst / 100)))) : ((r.amount - r.discountValue) * (r.igst / 100));
        }
      });
      taxPercent = cgst + sgst + igst;
      taxValue = cgstVal + sgstVal + igstVal;
      taxPercentDisplay = `GST (${taxPercent}%)`;
    }
    // Grand total: sum all net amounts
    grandTotal = itemRows.reduce((sum, r) => sum + r.netAmount, 0);
    grandTotal = Math.round(grandTotal);
    roundOff = grandTotal - itemRows.reduce((sum, r) => sum + r.netAmount, 0);
  }
  setInvoiceSummary({
    itemTotal,
    discount,
    subTotal,
    tax: taxValue,
    taxPercent: taxPercentDisplay,
    roundOff,
    grandTotal
  });
  
  // Update customer display when cart changes
  if (itemRows.length > 0) {
    customerDisplayManager.updateCart(itemRows, grandTotal);
  }
}, [itemRows, taxType])
  const [allItems, setAllItems] = useState([]) // from DB
  const [suggestions, setSuggestions] = useState({}) // {rowIndex: [items]}
  const [taxes, setTaxes] = useState([]); // All taxes from DB
  const [lastSavedInvoice, setLastSavedInvoice] = useState(null); // For printing
  const [companyProfile, setCompanyProfile] = useState(null); // Company profile from DB

  // const baseURL = 'http://localhost:4402';

  //const baseURL = 'https://www.sharmachefapi.cloudnetsoftwares.com'
   // const baseURL = 'https://www.chefmateapi.cloudnetsoftwares.com';
  const handleRowChange = (index, field, value) => {
    const updatedRows = [...itemRows]
    const row = updatedRows[index]
    row[field] = value

    // If editing itemName, update suggestions
    if (field === 'itemName') {
      if (value && value.trim().length > 0) {
        const filtered = allItems.filter(
          item =>
            item.iname &&
            item.iname.toLowerCase().includes(value.trim().toLowerCase())
        )
        setSuggestions(prev => ({ ...prev, [index]: filtered }))
      } else {
        setSuggestions(prev => ({ ...prev, [index]: [] }))
      }
    }

    // Tax-included/excluded logic (strict: amount is always base price, netAmount is final price paid)
    // Ensure taxIncluded is set if missing
    if (row.taxIncluded === undefined) {
      row.taxIncluded = taxes.length > 0 && taxes[0] && (taxes[0].included === true || taxes[0].included === 'true');
    }
    const included = row.taxIncluded;
    
    console.log('handleRowChange called:', {
      index,
      field,
      value,
      included,
      rate: row.rate,
      vat: row.vat,
      taxes: taxes.map(t => ({ id: t.id, name: t.taxname, value: t.taxvalue, included: t.included }))
    });
    let rate = parseFloat(row.rate) || 0;
    let quantity = parseFloat(row.quantity) || 1;
    let discountPercent = parseFloat(row.discountPercent) || 0;
    let vat = taxType === 'vat' ? (parseFloat(row.vat) || 0) : 0;
    let cgst = taxType === 'gst' ? (parseFloat(row.cgst) || 0) : 0;
    let sgst = taxType === 'gst' ? (parseFloat(row.sgst) || 0) : 0;
    let igst = taxType === 'gst' ? (parseFloat(row.igst) || 0) : 0;
    let totalTaxPercent = taxType === 'vat' ? vat : (cgst + sgst + igst);
    let amount = 0, discountValue = 0, netAmount = 0;

    if (included) {
      // Tax is included in price: amount = rate * quantity, net = amount - discount (NO TAX ADDED)
      amount = rate * quantity; // Full tax-inclusive amount
      discountValue = amount * (discountPercent / 100); // Discount on full amount  
      netAmount = amount - discountValue; // Net amount after discount (NO TAX ADDED!)
      console.log('handleRowChange - Tax Included:', { rate, quantity, amount, discountValue, netAmount });
    } else {
      // Tax is excluded: add tax after discount
      amount = rate * quantity;
      discountValue = amount * (discountPercent / 100);
      netAmount = (amount - discountValue) * (1 + (totalTaxPercent / 100));
    }

    row.amount = amount;
    row.discountValue = discountValue;
    row.netAmount = netAmount;

    console.log('handleRowChange result:', {
      index,
      included,
      amount,
      netAmount,
      discountValue,
      calculation: included ? 'Tax Included: net = amount - discount' : 'Tax Excluded: net = (amount - discount) * (1 + tax%)'
    });

    setItemRows(updatedRows)
  }
  // Add global shortcut for adding new row (Ctrl+Enter for Windows/Linux, Cmd+Enter for Mac)
  // and removing last row added by shortcut (Ctrl+Del)
  useEffect(() => {
    const handleShortcut = e => {
      // For Windows/Linux: Ctrl+Enter, for Mac: Meta+Enter
      const isCtrlEnter = e.ctrlKey && e.key === 'Enter'
      const isCmdEnter = e.metaKey && e.key === 'Enter'
      const isCtrlDel = e.ctrlKey && (e.key === 'Delete' || e.key === 'Del')
      if (isCtrlEnter || isCmdEnter) {
        e.preventDefault()
        setItemRows(prev => {
          const newRows = [
            ...prev,
            {
              id: Date.now(),
              itemName: '',
              description: '',
              rate: 0,
              quantity: 1,
              discountPercent: 0,
              cgst: 0,
              sgst: 0,
              igst: 0,
              amount: 0,
              discountValue: 0,
              netAmount: 0,
              taxIncluded: taxes.length > 0 && taxes[0] && (taxes[0].included === true || taxes[0].included === 'true')
            }
          ]
          setEditableRowIndex(newRows.length - 1)
          return newRows
        })
      } else if (isCtrlDel) {
        e.preventDefault()
        setItemRows(prev => {
          if (prev.length === 0) return prev
          const newRows = prev.slice(0, -1)
          setEditableRowIndex(newRows.length - 1)
          return newRows
        })
      }
    }
    window.addEventListener('keydown', handleShortcut)
    return () => window.removeEventListener('keydown', handleShortcut)
  }, [])

  // Focus the react-select input when editableRowIndex changes to the last row
  useEffect(() => {
    if (
      editableRowIndex === itemRows.length - 1 &&
      itemNameRefs.current[editableRowIndex]
    ) {
      // Try to focus react-select input
      const select = itemNameRefs.current[editableRowIndex]
      if (select && select.focus) {
        select.focus()
      } else if (select && select.select && select.select.inputRef) {
        select.select.inputRef.focus()
      } else {
        // Fallback: try to find the input inside react-select
        const input = document.querySelector(
          `#itemName-${editableRowIndex} input`
        )
        if (input) input.focus()
      }
    }
  }, [editableRowIndex, itemRows.length])

  const addNewRow = (focus = false) => {
    setItemRows(prev => {
      const newRows = [
        ...prev,
        {
          id: Date.now(),
          itemName: '',
          description: '',
          rate: 0,
          quantity: 1,
          discountPercent: 0,
          cgst: 0,
          sgst: 0,
          igst: 0,
          amount: 0,
          discountValue: 0,
          netAmount: 0,
          taxIncluded: taxes.length > 0 && taxes[0] && (taxes[0].included === true || taxes[0].included === 'true')
        }
      ]
      if (focus) {
        setTimeout(() => {
          const nextIndex = newRows.length - 1
          setEditableRowIndex(nextIndex)
          if (itemNameRefs.current[nextIndex]) {
            itemNameRefs.current[nextIndex].focus()
          }
        }, 100)
      }
      return newRows
    })
  }

  const deleteRow = index => {
    const updated = [...itemRows]
    updated.splice(index, 1)
    setItemRows(updated)
  }
  const handleBillHistory = async () => {
    navigate(`/reports/billhistory`);
  };
  const handlenewCustomer = async () => {
    navigate(`/master/newcustomer`);
  };
  const handleScan = async e => {
  e.preventDefault();
  if (!barcode) return;
  try {
    // Use fetchData to get item by item_code
    const itemsArr = await fetchData('items', () => {}, 'id', { item_code: barcode });
    const item = itemsArr && itemsArr[0];
    if (!item || item.stock <= 0) {
      toast.error('Item not found or out of stock.');
      return;
    }
    // Check if item already exists in itemRows
    const existingIndex = itemRows.findIndex(r => r.itemName === item.iname);
    if (existingIndex !== -1) {
      // If exists, increment quantity
      const updatedRows = [...itemRows];
      updatedRows[existingIndex].quantity += 1;
      
      // Check if tax is included from database configuration
      const taxIncluded = taxes.length > 0 && taxes[0] && (taxes[0].included === true || taxes[0].included === 'true');
      
      // Recalculate based on tax inclusion setting
      const rate = updatedRows[existingIndex].rate;
      const quantity = updatedRows[existingIndex].quantity;
      const discountPercent = updatedRows[existingIndex].discountPercent || 0;
      
      if (taxIncluded) {
        // Tax included: net amount = amount - discount (no tax added)
        updatedRows[existingIndex].amount = rate * quantity;
        updatedRows[existingIndex].discountValue = updatedRows[existingIndex].amount * (discountPercent / 100);
        updatedRows[existingIndex].netAmount = updatedRows[existingIndex].amount - updatedRows[existingIndex].discountValue;
        updatedRows[existingIndex].taxIncluded = true;
      } else {
        // Tax excluded: add tax on top
        updatedRows[existingIndex].amount = rate * quantity;
        updatedRows[existingIndex].discountValue = updatedRows[existingIndex].amount * (discountPercent / 100);
        
        const taxedAmount = updatedRows[existingIndex].amount - updatedRows[existingIndex].discountValue;
        const totalTaxPercent = (updatedRows[existingIndex].cgst || 0) + (updatedRows[existingIndex].sgst || 0) + (updatedRows[existingIndex].igst || 0) + (updatedRows[existingIndex].vat || 7);
        const totalTax = taxedAmount * (totalTaxPercent / 100);
        updatedRows[existingIndex].netAmount = taxedAmount + totalTax;
        updatedRows[existingIndex].taxIncluded = false;
      }
      
      console.log('Barcode scan - existing item updated:', {
        itemName: updatedRows[existingIndex].itemName,
        taxIncluded,
        amount: updatedRows[existingIndex].amount,
        netAmount: updatedRows[existingIndex].netAmount
      });

      setItemRows(updatedRows);
    } else {
      // Add new row to itemRows
      const basePrice = item.offerprice || 0;
      const discountPercent = 0;
      const discountValue = 0;
      
      // Check if tax is included from database configuration
      const taxIncluded = taxes.length > 0 && taxes[0] && (taxes[0].included === true || taxes[0].included === 'true');
      
      let netAmount;
      if (taxIncluded) {
        // Tax included: net amount = base price (no tax added)
        netAmount = basePrice - discountValue;
      } else {
        // Tax excluded: add tax on top
        const taxedAmount = basePrice - discountValue;
        const totalTaxPercent = (item.cgst || 0) + (item.sgst || 0) + (item.igst || 0) + (item.vat || 7);
        const totalTax = taxedAmount * (totalTaxPercent / 100);
        netAmount = taxedAmount + totalTax;
      }
      
      console.log('Barcode scan - new item added:', {
        itemName: item.iname,
        taxIncluded,
        basePrice,
        netAmount
      });

      setItemRows(prev => [
        ...prev,
        {
          id: Date.now(),
          itemName: item.iname,
          description: item.description || '',
          rate: basePrice,
          quantity: 1,
          amount: basePrice,
          discountPercent,
          discountValue,
          cgst: item.cgst || 0,
          sgst: item.sgst || 0,
          igst: item.igst || 0,
          vat: item.vat || 7,
          netAmount,
          taxIncluded: taxIncluded,
          taxIncluded: taxes.length > 0 && taxes[0] && (taxes[0].included === true || taxes[0].included === 'true')
        }
      ]);
    }
    setBarcode('');
  } catch (error) {
    toast.error('Error fetching item.');
  }
};

// Toggle customer display
const toggleCustomerDisplay = () => {
  if (isCustomerDisplayOpen) {
    customerDisplayManager.closeCustomerDisplay();
    setIsCustomerDisplayOpen(false);
  } else {
    customerDisplayManager.openSaleCustomerDisplay();
    setIsCustomerDisplayOpen(true);
    // Send current cart to display
    if (itemRows.length > 0) {
      customerDisplayManager.updateCart(itemRows, invoiceSummary.grandTotal);
    }
  }
};

  // Customer auto-suggestion state
  const [allCustomers, setAllCustomers] = useState([]) // All customers from DB

  // Fetch all customers for auto-suggestion
  useEffect(() => {
    fetchData('customers', setAllCustomers, 'id', {})
    fetchData('bank_accounts', setBankAccounts, 'id', {})
  }, [])
  // Print bill in A4 format using DB fetches (final_bill, order_items_gst, customers)
  // Accepts optional billId; if not provided, fetches latest bill

  // sale.js - Updated POS Component with barcode scanning and streamlined checkout features
  useEffect(() => {
    const newTotal = cart.reduce(
      (sum, item) => sum + item.offerprice * item.quantity,
      0
    )
    setTotal(newTotal)
  }, [cart])

  const calculateFinalTotal = () => {
    let discount = 0
    if (discountType === 'percentage') {
      discount = total * (discountValue / 100)
    } else {
      discount = discountValue
    }
    return Math.max(0, total - discount)
  }

  useEffect(() => {
    fetchData('items', setAllItems, 'id', {});
    fetchData('taxes', setTaxes, 'id', {});
    // Fetch company profile
    fetchData('company_profile', (data) => {
      if (data && data.length > 0) {
        setCompanyProfile(data[0]);
      }
    }, 'id', {});
  }, [])

  const handleItemSelect = (index, selectedItem) => {
    const updatedRows = [...itemRows];
    // Find tax row for this item (by tax name or value, fallback to first)
    let taxRow = null;
    if (taxType === 'vat') {
      taxRow = taxes.find(t => t.taxname && selectedItem.vat && (parseFloat(t.taxvalue) === parseFloat(selectedItem.vat)));
    } else {
      // For GST, try to match by cgst/sgst/igst or fallback
      taxRow = taxes.find(t => t.taxname && (
        (selectedItem.cgst && parseFloat(t.taxvalue) === parseFloat(selectedItem.cgst)) ||
        (selectedItem.sgst && parseFloat(t.taxvalue) === parseFloat(selectedItem.sgst)) ||
        (selectedItem.igst && parseFloat(t.taxvalue) === parseFloat(selectedItem.igst))
      ));
    }
    if (!taxRow && taxes.length > 0) taxRow = taxes[0];
    const included = taxRow && (taxRow.included === true || taxRow.included === 'true');
    console.log('Tax calculation debug:', {
      selectedItem: selectedItem.iname,
      taxRow: taxRow,
      included: included,
      taxType: taxType,
      rate: selectedItem.offerprice,
      taxesFromDB: taxes.map(t => ({ id: t.id, name: t.taxname, value: t.taxvalue, included: t.included }))
    });

    // Calculate price/tax logic (fix: for tax-included, netAmount = gross - discount on base, amount = base, tax = diff)
    let rate = selectedItem.offerprice || 0;
    let quantity = 1;
    let amount = 0;
    let discountPercent = 0;
    let discountValue = 0;
    let vat = taxType === 'vat' ? (selectedItem.vat || 7) : 0;
    let cgst = taxType === 'gst' ? (selectedItem.cgst || 0) : 0;
    let sgst = taxType === 'gst' ? (selectedItem.sgst || 0) : 0;
    let igst = taxType === 'gst' ? (selectedItem.igst || 0) : 0;
    let netAmount = 0;

    if (included) {
      // Tax included: the rate already includes tax - netAmount should equal amount
      amount = rate * quantity; // Amount is the full price (tax-inclusive) 
      discountValue = amount * (discountPercent / 100); // Discount on full amount
      netAmount = amount - discountValue; // Net amount = amount - discount (NO TAX ADDED!)
      console.log('Tax Included Calculation:', { rate, quantity, amount, discountValue, netAmount });
    } else {
      // Tax excluded: need to add tax on top
      amount = rate * quantity;
      discountValue = amount * (discountPercent / 100);
      if (taxType === 'vat') {
        netAmount = (amount - discountValue) * (1 + (vat / 100));
      } else {
        const totalTaxPercent = cgst + sgst + igst;
        netAmount = (amount - discountValue) * (1 + (totalTaxPercent / 100));
      }
    }

    updatedRows[index] = {
      ...updatedRows[index],
      itemName: selectedItem.iname,
      description: selectedItem.description || '',
      rate,
      quantity,
      amount,
      discountPercent,
      discountValue,
      vat,
      cgst,
      sgst,
      igst,
      netAmount,
      taxIncluded: included,
    };
    
    console.log('Item added to cart:', {
      itemName: selectedItem.iname,
      rate,
      amount, // Tax-inclusive amount
      netAmount, // Amount after discount (still tax-inclusive)
      taxIncluded: included,
      taxPercent: taxType === 'vat' ? vat : (cgst + sgst + igst),
      discountValue,
      calculation: included ? 'Tax Included: net = amount - discount' : 'Tax Excluded: net = (amount - discount) * (1 + tax)',
      taxRowFound: !!taxRow,
      taxRowIncluded: taxRow ? taxRow.included : 'no tax row'
    });
    setItemRows(updatedRows);
    setSuggestions(prev => ({ ...prev, [index]: [] })); // close suggestion
    setTimeout(() => {
      // Focus description field after selection
      const descInput = document.getElementById(`description-${index}`);
      if (descInput) descInput.focus();
    }, 0);
  };

  // Helper function to convert BLOB to base64
  const blobToBase64 = (blob, mimeType) => {
    if (!blob) return null;
    try {
      let uint8Array;
      if (blob.data) {
        uint8Array = new Uint8Array(blob.data);
      } else if (Array.isArray(blob)) {
        uint8Array = new Uint8Array(blob);
      } else {
        uint8Array = new Uint8Array(blob);
      }
      const base64 = btoa(String.fromCharCode.apply(null, uint8Array));
      return `data:${mimeType};base64,${base64}`;
    } catch (error) {
      console.error('Error converting BLOB to base64:', error);
      return null;
    }
  };

  const printBill = () => {
    // Build invoice data from current state
    const finalTotal = calculateFinalTotal();
    
    const currentInvoiceData = {
      invoice_number: modalInvoiceData.invoice_number || 'DRAFT',
      setup_date: new Date(),
      payment_method: paymentType,
      customer_name: customer.name || customerDetails.name || 'Walk-in Customer',
      customer_phone: customer.phone || customerDetails.phone || 'N/A',
      customer_email: customer.email || customerDetails.email || '',
      customer_address: customer.address || customerDetails.address || '',
      customer_gst: customer.gst || customerDetails.gst || '',
      items: itemRows.map(row => ({
        item_name: row.itemName,
        rate: row.rate,
        quantity: row.quantity,
        discount_percent: row.discountPercent,
        total_amount: row.netAmount
      })),
      subtotal: invoiceSummary.itemTotal,
      discount_value: discountValue,
      tax: invoiceSummary.tax,
      round_off: invoiceSummary.roundOff,
      grand_total: invoiceSummary.grandTotal
    };

    // Use current data or fallback to lastSavedInvoice
    const invoiceData = lastSavedInvoice || currentInvoiceData;
    
    if (!invoiceData) {
      toast.error('No invoice data available for printing. Please add items first.');
      return;
    }

    // Use invoice_number for display, fallback to id if invoice_number is not available
    const invoiceNumber = invoiceData.invoice_number || invoiceData.id || 'DRAFT';

    // Get company details with fallback
    const companyName = companyProfile?.name || 'CloudNet Softwares';
    const companyPhone = companyProfile?.phone_number || '+66-948712350/+66-952477020';
    const companyEmail = companyProfile?.email || 'info@cloudnetsoftwares.com';
    const companyWebsite = companyProfile?.website || 'www.cloudnetsoftwares.com';
    const companyAddress = companyProfile?.address || '123 Beach Road, Pattaya, Thailand';
    const companyCity = companyProfile?.city || '';
    const companyState = companyProfile?.state || '';
    const companyZip = companyProfile?.zip_code || '';
    const companyCountry = companyProfile?.country || '';
    const fullAddress = [companyAddress, companyCity, companyState, companyZip, companyCountry].filter(Boolean).join(', ');
    
    // Get logo if available
    const logoUrl = companyProfile?.logo && companyProfile?.logo_type 
      ? blobToBase64(companyProfile.logo, companyProfile.logo_type) 
      : '/logo.png';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${invoiceNumber}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 15px;
            background: white;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 15px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }
          .header-left {
            display: flex;
            align-items: center;
            gap: 15px;
          }
          .logo {
            width: 140px;
            height: 45px;
            object-fit: contain;
          }
          .company-info-left {
            display: flex;
            flex-direction: column;
          }
          .company-logo {
            color: #FF6B35;
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 2px;
          }
          .company-tagline {
            color: #666;
            font-size: 11px;
            line-height: 1.2;
            font-weight: 600;
          }
          .header-right {
            text-align: right;
            font-size: 11px;
            color: #333;
            line-height: 1.3;
          }
          .invoice-info {
            display: flex;
            justify-content: space-between;
            margin: 10px 0;
            gap: 15px;
          }
          .invoice-details, .customer-details {
            width: 48%;
          }
          .section-title {
            font-weight: bold;
            font-size: 12px;
            margin-bottom: 5px;
            color: #333;
            border-bottom: 1px solid #ddd;
            padding-bottom: 3px;
          }
          .info-row {
            font-size: 10px;
            margin: 3px 0;
            color: #555;
            line-height: 1.3;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
            font-size: 11px;
          }
          .items-table th,
          .items-table td {
            border: 1px solid #ddd;
            padding: 5px 6px;
            text-align: left;
          }
          .items-table th {
            background-color: #f8f9fa;
            font-weight: bold;
            color: #333;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .summary-section {
            margin-top: 10px;
            display: flex;
            justify-content: flex-end;
          }
          .summary-table {
            width: 280px;
            font-size: 11px;
          }
          .summary-table td {
            padding: 4px 8px;
            border: none;
          }
          .summary-table .total-row {
            font-weight: bold;
            border-top: 2px solid #333;
            font-size: 13px;
          }
          .footer {
            margin-top: 20px;
            font-size: 9px;
            color: #666;
            text-align: center;
          }
          .signature-section {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-top: 20px;
            padding-top: 10px;
          }
          .signature-left {
            flex: 1;
            text-align: left;
          }
          .signature-right {
            flex: 1;
            text-align: right;
            display: flex;
            justify-content: flex-end;
          }
          .authorised-signatory {
            text-align: center;
            min-width: 200px;
          }
          .signature-space {
            height: 40px;
            width: 150px;
            margin-bottom: 3px;
          }
          .signature-line {
            border-bottom: 1px solid #333;
            width: 150px;
            margin: 0 auto 3px auto;
          }
          .signature-text {
            font-size: 10px;
            color: #333;
            font-weight: bold;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <div class="company-info-left">
              <div class="company-logo"><img src="${logoUrl}" alt="${companyName} Logo" class="logo" onerror="this.style.display='none'" /></div>
              <div class="company-tagline">
                ${companyName}
              </div>
            </div>
          </div>
          <div class="header-right">
            📞 ${companyPhone}<br>
            🌐 ${companyWebsite}<br>
            📧 ${companyEmail}<br>
            📍 ${fullAddress}
          </div>
        </div>

        <div class="invoice-info">
          <div class="invoice-details">
            <div class="section-title">Invoice Details</div>
            <div class="info-row"><strong>Invoice No:</strong> ${invoiceNumber}</div>
            <div class="info-row"><strong>Date:</strong> ${new Date(invoiceData.setup_date || Date.now()).toLocaleDateString('en-GB')}</div>
            <div class="info-row"><strong>Payment Method:</strong> ${invoiceData.payment_method || 'Cash'}</div>
          </div>
          <div class="customer-details">
            <div class="section-title">Customer Details</div>
            <div class="info-row"><strong>Name:</strong> ${invoiceData.customer_name || 'Walk-in Customer'}</div>
            <div class="info-row"><strong>Phone:</strong> ${invoiceData.customer_phone || 'N/A'}</div>
            ${invoiceData.customer_email ? `<div class="info-row"><strong>Email:</strong> ${invoiceData.customer_email}</div>` : ''}
            ${invoiceData.customer_address ? `<div class="info-row"><strong>Address:</strong> ${invoiceData.customer_address}</div>` : ''}
            ${invoiceData.customer_gst ? `<div class="info-row"><strong>Tax ID:</strong> ${invoiceData.customer_gst}</div>` : ''}
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th width="5%" class="text-center">S.No</th>
              <th width="35%">Item Name</th>
              <th width="15%" class="text-right">Rate</th>
              <th width="10%" class="text-center">Qty</th>
              <th width="15%" class="text-right">Amount</th>
              <th width="10%" class="text-right">Disc%</th>
              <th width="20%" class="text-right">Net Amount</th>
            </tr>
          </thead>
          <tbody>
            ${invoiceData.items && invoiceData.items.length > 0 ? invoiceData.items.map((item, index) => `
              <tr>
                <td class="text-center">${index + 1}</td>
                <td>${item.item_name || item.iname || 'N/A'}</td>
                <td class="text-right">฿ ${((item.rate || item.offerprice || 0)).toFixed(2)}</td>
                <td class="text-center">${item.quantity || 0}</td>
                <td class="text-right">฿ ${((item.rate || item.offerprice || 0) * (item.quantity || 1)).toFixed(2)}</td>
                <td class="text-right">${item.discount_percent || 0}%</td>
                <td class="text-right">฿ ${(item.total_amount || item.quantity * item.offerprice || 0).toFixed(2)}</td>
              </tr>
            `).join('') : '<tr><td colspan="7" class="text-center">No items found</td></tr>'}
          </tbody>
        </table>

        <div class="summary-section">
          <table class="summary-table">
            <tr>
              <td>Subtotal:</td>
              <td class="text-right">฿ ${(invoiceData.subtotal || invoiceData.total || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td>Discount:</td>
              <td class="text-right">฿ ${(invoiceData.discount_value || 0).toFixed(2)}</td>
            </tr>
            ${showVatDetails ? `
            <tr>
              <td>Tax(7%):</td>
              <td class="text-right">฿ ${(invoiceData.tax || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td>Round Off:</td>
              <td class="text-right">฿ ${(invoiceData.round_off || 0).toFixed(2)}</td>
            </tr>
            ` : ''}
            <tr class="total-row">
              <td><strong>Grand Total:</strong></td>
              <td class="text-right"><strong>฿ ${(invoiceData.grand_total || invoiceData.final_total || 0).toFixed(2)}</strong></td>
            </tr>
          </table>
        </div>

        <div class="signature-section">
          <div class="signature-left">
            <div style="font-size: 10px; color: #666; text-align: left;">
              <p>Thank you for your business!</p>
              <p>This is a computer generated invoice.</p>
            </div>
          </div>
          <div class="signature-right">
            <div class="authorised-signatory">
              <div class="signature-space"></div>
              <div class="signature-line"></div>
              <div class="signature-text">Authorised Signatory</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Auto print after a short delay
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // New VAT Format Print Bill Function
  const printBillVATFormat = async (reprintInvoiceId = null) => {
    // Fetch coresetting to check showVat
    let showVatDetails = false;
    try {
      const coreSettingResponse = await fetchData("coresetting", () => {}, "id", {});
      if (coreSettingResponse && coreSettingResponse.length > 0) {
        showVatDetails = coreSettingResponse[0].showVat?.toLowerCase() === 'yes';
      }
    } catch (error) {
      console.error('Error fetching coresetting:', error);
    }

    let invoiceData;

    // If reprintInvoiceId is provided, fetch from database
    if (reprintInvoiceId) {
      try {
        const billResponse = await fetchData("final_bill", () => {}, "id", { id: reprintInvoiceId });
        if (!billResponse || billResponse.length === 0) {
          toast.error('Invoice not found');
          return;
        }
        const billData = billResponse[0];

        const itemsResponse = await fetchData("order_items", () => {}, "order_id", { order_id: reprintInvoiceId });

        // Fetch customer details using customer_id
        let customerDetails = {
          name: 'Walk-in Customer',
          contact: 'N/A',
          email: '',
          address: '',
          taxid: ''
        };
        
        if (billData.customer_id) {
          const customerResponse = await fetchData("customers", () => {}, "id", { id: billData.customer_id });
          if (customerResponse && customerResponse.length > 0) {
            customerDetails = customerResponse[0];
          }
        }

        invoiceData = {
          invoice_number: billData.id,
          setup_date: billData.setup_date,
          payment_method: billData.payment_mode,
          customer_name: customerDetails.name || 'Walk-in Customer',
          customer_phone: customerDetails.contact || 'N/A',
          customer_email: customerDetails.email || '',
          customer_address: customerDetails.address || '',
          customer_gst: customerDetails.taxid || '',
          items: itemsResponse.map(item => ({
            item_name: item.item_name,
            description: item.description || '',
            rate: parseFloat(item.rate || item.offerprice || 0),
            quantity: parseFloat(item.quantity || 0),
            discount_percent: parseFloat(item.discount_percent || 0),
            vat: parseFloat(item.vat || 7),
            total_amount: parseFloat(item.total_price || 0)
          })),
          subtotal: parseFloat(billData.subtotal || 0),
          discount_value: parseFloat(billData.discount_value || 0),
          tax: parseFloat(billData.tax || 0),
          round_off: parseFloat(billData.round_off || 0),
          grand_total: parseFloat(billData.grand_total || 0)
        };
      } catch (error) {
        console.error('Error fetching invoice data:', error);
        toast.error('Failed to load invoice data');
        return;
      }
    } else {
      // Build invoice data from current state
      const finalTotal = calculateFinalTotal();
      
      const currentInvoiceData = {
        invoice_number: window.lastSavedBillId || modalInvoiceData.invoice_number || 'DRAFT',
        setup_date: new Date(),
        payment_method: paymentType,
        customer_name: customer.name || customerDetails.name || 'Walk-in Customer',
        customer_phone: customer.phone || customerDetails.phone || 'N/A',
        customer_email: customer.email || customerDetails.email || '',
        customer_address: customer.address || customerDetails.address || '',
        customer_gst: customer.gst || customerDetails.gst || '',
        items: itemRows.map(row => ({
          item_name: row.itemName,
          description: row.description || '',
          rate: row.rate,
          quantity: row.quantity,
          discount_percent: row.discountPercent,
          vat: row.vat || 7,
          total_amount: row.netAmount
        })),
        subtotal: invoiceSummary.itemTotal,
        discount_value: discountValue,
        tax: invoiceSummary.tax,
        round_off: invoiceSummary.roundOff,
        grand_total: invoiceSummary.grandTotal
      };

      // Use lastSavedInvoice first (for Save & Print), then fallback to current data
      invoiceData = lastSavedInvoice || currentInvoiceData;
    }
    
    if (!invoiceData) {
      toast.error('No invoice data available for printing. Please add items first.');
      return;
    }

    const invoiceNumber = invoiceData.invoice_number || invoiceData.id || 'DRAFT';

    // Get company details with fallback
    const companyName = companyProfile?.name || 'CloudNet Softwares';
    const companyPhone = companyProfile?.phone_number || '+66-948712350/+66-952477020';
    const companyEmail = companyProfile?.email || 'info@cloudnetsoftwares.com';
    const companyWebsite = companyProfile?.website || 'www.cloudnetsoftwares.com';
    const companyAddress = companyProfile?.address || '123 Beach Road, Pattaya, Thailand';
    const companyCity = companyProfile?.city || '';
    const companyState = companyProfile?.state || '';
    const companyZip = companyProfile?.zip_code || '';
    const companyCountry = companyProfile?.country || '';
    const companyTaxId = companyProfile?.tax_id || '';
    const fullAddress = [companyAddress, companyCity, companyState, companyZip, companyCountry].filter(Boolean).join(', ');
    
    // Get logo if available
    const logoUrl = companyProfile?.logo && companyProfile?.logo_type 
      ? blobToBase64(companyProfile.logo, companyProfile.logo_type) 
      : '/logo.png';

    // Get QR code if available
    const qrCodeUrl = companyProfile?.qr_code && companyProfile?.qr_code_type 
      ? blobToBase64(companyProfile.qr_code, companyProfile.qr_code_type) 
      : null;

    // Get terms and conditions
    const termsAndConditions = companyProfile?.terms_and_conditions || `Terms & Conditions:
• All prices are inclusive of VAT
• Payment is due upon receipt
• Goods once sold cannot be returned`;

    // Function to generate invoice HTML for each copy
    const generateInvoiceHTML = (copyLabel) => `
        <div class="vat-invoice-container">
         
          <div class="copy-label">${copyLabel}</div>
          <div class="header-section">
            <div class="company-header">
              <div class="company-logo-section">
                ${companyProfile?.logo && companyProfile?.logo_type ? `
                  <img src="${logoUrl}" alt="${companyName} Logo" style="max-width: 140px; max-height: 60px; margin-bottom: 6px;" onerror="this.style.display='none'" />
                ` : ''}
                <div class="company-name">${companyName}</div>
                ${companyTaxId ? `<div class="company-tagline">Tax ID: ${companyTaxId}</div>` : ''}
              </div>
              <div class="company-contact">
                <div>📞 ${companyPhone}</div>
                <div>🌐 ${companyWebsite}</div>
                <div>📧 ${companyEmail}</div>
                <div style="margin-top: 8px;">📍 ${fullAddress}</div>
              </div>
            </div>
          </div>

          <div class="invoice-title">🧾 INVOICE</div>

          <div class="info-grid">
            <div class="info-box">
              <div class="info-box-title">📋 Invoice Details</div>
              <div class="info-row">
                <span class="info-label">Invoice No:</span>
                <span class="info-value">${invoiceNumber}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Date:</span>
                <span class="info-value">${new Date(invoiceData.setup_date).toLocaleDateString('en-GB')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Time:</span>
                <span class="info-value">${new Date(invoiceData.setup_date).toLocaleTimeString('en-GB')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment:</span>
                <span class="info-value">${invoiceData.payment_method || 'Cash'}</span>
              </div>
            </div>

            <div class="info-box">
              <div class="info-box-title">👤 Customer Details</div>
              <div class="info-row">
                <span class="info-label">Name:</span>
                <span class="info-value">${invoiceData.customer_name || 'Walk-in Customer'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Phone:</span>
                <span class="info-value">${invoiceData.customer_phone || 'N/A'}</span>
              </div>
              ${invoiceData.customer_email ? `
              <div class="info-row">
                <span class="info-label">Email:</span>
                <span class="info-value">${invoiceData.customer_email}</span>
              </div>
              ` : ''}
              ${invoiceData.customer_address ? `
              <div class="info-row">
                <span class="info-label">Address:</span>
                <span class="info-value">${invoiceData.customer_address}</span>
              </div>
              ` : ''}
              ${invoiceData.customer_gst ? `
              <div class="info-row">
                <span class="info-label">Tax ID:</span>
                <span class="info-value">${invoiceData.customer_gst}</span>
              </div>
              ` : ''}
            </div>
          </div>

          <div class="items-wrapper">
            <table class="items-table">
              <thead>
                <tr>
                  <th style="width: 5%;">#</th>
                  <th style="width: 35%;">Item Description</th>
                  <th style="width: 10%;" class="text-right">Rate</th>
                  <th style="width: 8%;" class="text-center">Qty</th>
                  ${showVatDetails ? '<th style="width: 10%;" class="text-right">Discount</th>' : ''}
                  ${showVatDetails ? '<th style="width: 8%;" class="text-center">VAT%</th>' : ''}
                  <th style="width: 12%;" class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${invoiceData.items.map((item, index) => `
                  <tr>
                    <td class="text-center">${index + 1}</td>
                    <td>${item.item_name}${item.description ? `<br><i style="font-size: 8px; color: #666;">${item.description}</i>` : ''}</td>
                    <td class="text-right">฿ ${parseFloat(item.rate || item.offerprice || 0).toFixed(2)}</td>
                    <td class="text-center">${parseFloat(item.quantity || 0).toFixed(2)}</td>
                    ${showVatDetails ? `<td class="text-right">${parseFloat(item.discount_percent || 0).toFixed(2)}%</td>` : ''}
                    ${showVatDetails ? `<td class="text-center">${parseFloat(item.vat || 0).toFixed(0)}%</td>` : ''}
                    <td class="text-right">฿ ${parseFloat(item.total_amount || 0).toFixed(2)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <hr style="border: none; border-top: 3px solid #000; margin: 15px 0;" />

          <div class="summary-section">
            <div class="summary-box">
              <div class="summary-row">
                <span class="summary-label">Subtotal:</span>
                <span class="summary-value">฿ ${parseFloat(invoiceData.subtotal || invoiceData.total || 0).toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">Discount:</span>
                <span class="summary-value">- ฿ ${parseFloat(invoiceData.discount_value || 0).toFixed(2)}</span>
              </div>
              ${showVatDetails ? `
              <div class="summary-row">
                <span class="summary-label">Subtotal After Discount:</span>
                <span class="summary-value">฿ ${(parseFloat(invoiceData.subtotal || 0) - parseFloat(invoiceData.discount_value || 0)).toFixed(2)}</span>
              </div>
              <div class="summary-row vat-row">
                <span class="summary-label">🔖 VAT (7%):</span>
                <span class="summary-value">฿ ${parseFloat(invoiceData.tax || 0).toFixed(2)}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">Round Off:</span>
                <span class="summary-value">฿ ${parseFloat(invoiceData.round_off || 0).toFixed(2)}</span>
              </div>
              ` : ''}
              <div class="summary-row total-row">
                <span class="summary-label">GRAND TOTAL:</span>
                <span class="summary-value">฿ ${parseFloat(invoiceData.grand_total || invoiceData.final_total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div class="footer-section">
            <div class="terms-section">
              <div class="terms-title">Terms & Conditions:</div>
              ${termsAndConditions.split('\n').map(line => `<div>${line}</div>`).join('')}
              <div style="margin-top: 15px; font-size: 10px; font-style: italic;">
                This is a computer-generated invoice and does not require a physical signature.
              </div>
            </div>
            ${(companyProfile?.bank_name || companyProfile?.account_number) ? `
            <div class="bank-details-box" style="width: 250px; border: 1px solid #e0e0e0; padding: 8px; border-radius: 5px; background: #f8f9fa; margin: 0 15px;">
              <div style="font-weight: bold; font-size: 10px; color: #000; border-bottom: 2px solid #FF8A80; padding-bottom: 3px; margin-bottom: 4px;">
                🏦 Bank Details
              </div>
              ${companyProfile?.bank_name ? `
              <div style="font-size: 8px; padding: 2px 0;">
                <span style="font-weight: 600; color: #555;">Bank Name:</span>
                <span style="color: #333; margin-left: 5px;">${companyProfile.bank_name}</span>
              </div>` : ''}
              ${companyProfile?.account_name ? `
              <div style="font-size: 8px; padding: 2px 0;">
                <span style="font-weight: 600; color: #555;">Account Name:</span>
                <span style="color: #333; margin-left: 5px;">${companyProfile.account_name}</span>
              </div>` : ''}
              ${companyProfile?.account_number ? `
              <div style="font-size: 8px; padding: 2px 0;">
                <span style="font-weight: 600; color: #555;">Account Number:</span>
                <span style="color: #333; margin-left: 5px;">${companyProfile.account_number}</span>
              </div>` : ''}
              ${companyProfile?.routing_number ? `
              <div style="font-size: 8px; padding: 2px 0;">
                <span style="font-weight: 600; color: #555;">Routing Number:</span>
                <span style="color: #333; margin-left: 5px;">${companyProfile.routing_number}</span>
              </div>` : ''}
              ${companyProfile?.swift_code ? `
              <div style="font-size: 8px; padding: 2px 0;">
                <span style="font-weight: 600; color: #555;">SWIFT Code:</span>
                <span style="color: #333; margin-left: 5px;">${companyProfile.swift_code}</span>
              </div>` : ''}
            </div>
            ` : ''}
            <div class="signature-box">
              <div class="signature-line">
                Authorized Signatory
              </div>
            </div>
          </div>
        </div>
        <div class="page-break"></div>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>VAT Invoice - ${invoiceNumber}</title>
        <style>
          @page { size: A4; margin: 8mm; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 0;
            padding: 8px;
            background: white;
            color: #333;
          }
          .page-break {
            page-break-after: always;
          }
          .copy-label {
            text-align: right;
            font-size: 12px;
            font-weight: bold;
            color: #FF8A80;
            margin-top: 35px;
            margin-bottom: 5px;
            text-transform: uppercase;
          }
          .vat-invoice-container {
            max-width: 210mm;
            min-height: 270mm;
            margin: 0 auto;
            border: 2px solid #000;
            padding: 10px;
            display: flex;
            flex-direction: column;
            margin-bottom: 20px;
          }
          .header-section {
            border-bottom: 3px solid #FF7F7F;
            padding-bottom: 6px;
            margin-bottom: 8px;
          }
          .company-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .company-logo-section {
            flex: 1;
          }
          .company-name {
            font-size: 13px;
            font-weight: bold;
            color: #050505ff;
            margin-bottom: 2px;
          }
          .company-tagline {
            font-size: 9px;
            color: #333;
            font-style: italic;
          }
          .company-contact {
            text-align: right;
            font-size: 14px;
            color: #0e0d0dff;
            line-height: 1.3;
          }
          .invoice-title {
            text-align: center;
            background: linear-gradient(135deg, #FF8A80 0%, #eb730a  100%);
            color: black;
            padding: 6px;
            margin: 6px 0;
            font-size: 16px;
            font-weight: bold;
            letter-spacing: 1px;
            border-radius: 4px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
            margin: 6px 0;
          }
          .info-box {
            border: 1px solid #e0e0e0;
            padding: 6px;
            border-radius: 5px;
            background: #f8f9fa;
          }
          .info-box-title {
            font-weight: bold;
            font-size: 11px;
            color: #000;
            border-bottom: 2px solid #FF8A80;
            padding-bottom: 3px;
            margin-bottom: 4px;
          }
          .info-row {
            display: flex;
            padding: 2px 0;
            font-size: 9px;
            line-height: 1.2;
          }
          .info-label {
            font-weight: 600;
            width: 90px;
            color: #555;
          }
          .info-value {
            flex: 1;
            color: #333;
          }
          .items-wrapper {
            flex: 1;
            display: flex;
            flex-direction: column;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 6px 0;
            font-size: 9px;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          }
          .items-table thead {
            background: linear-gradient(135deg, #b8b8b8ff 0%, #b8b8b8ff 100%);
            color: black;
          }
          .items-table th {
            padding: 5px 4px;
            text-align: left;
            font-weight: 600;
            border-right: 1px solid rgba(255,255,255,0.2);
          }
          .items-table th:last-child {
            border-right: none;
          }
          .items-table tbody tr {
            border-bottom: 1px solid #e0e0e0;
          }
          .items-table tbody tr:nth-child(even) {
            background-color: #f8f9fa;
          }
          .items-table tbody tr:hover {
            background-color: #e3f2fd;
          }
          .items-table td {
            padding: 4px 4px;
            vertical-align: middle;
          }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .summary-section {
            margin-top: auto;
            display: flex;
            justify-content: flex-end;
          }
          .summary-box {
            width: 280px;
            border: 2px solid #000;
            border-radius: 5px;
            overflow: hidden;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 10px;
            border-bottom: 1px solid #e0e0e0;
            font-size: 10px;
          }
          .summary-row:last-child {
            border-bottom: none;
          }
          .summary-label {
            font-weight: 600;
            color: #555;
          }
          .summary-value {
            font-weight: 500;
            color: #333;
          }
          .vat-row {
            background-color: #FFE5E5;
          }
          .total-row {
            background: linear-gradient(135deg, #FF8A80 0%, #eb730a  100%);
            color: white;
            font-size: 12px;
            font-weight: bold;
          }
          .footer-section {
            margin-top: 10px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            padding-top: 8px;
            border-top: 2px solid #FF8A80;
          }
          .terms-section {
            flex: 1;
            font-size: 8px;
            color: #666;
            line-height: 1.2;
          }
          .terms-title {
            font-weight: bold;
            margin-bottom: 3px;
            color: #FF8A80;
            font-size: 9px;
          }
          .signature-box {
            width: 160px;
            text-align: center;
          }
          .signature-line {
            border-top: 2px solid #000;
            margin-top: 30px;
            padding-top: 4px;
            font-weight: 600;
            color: #000;
            font-size: 9px;
          }
          .vat-badge {
            display: inline-block;
            background: #FF8A80;
            color: white;
            padding: 3px 8px;
            border-radius: 12px;
            font-size: 9px;
            font-weight: bold;
            margin-left: 8px;
          }
          .watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 120px;
            font-weight: bold;
            color: rgba(0, 0, 0, 0.08);
            pointer-events: none;
            z-index: 1000;
            white-space: nowrap;
          }
          @media print {
            body { margin: 0; padding: 10mm; }
            .vat-invoice-container { border: none; }
            @page { margin: 0; }
          }
        </style>
      </head>
      <body>
        ${generateInvoiceHTML('MERCHANT COPY')}
        ${generateInvoiceHTML('CUSTOMER COPY')}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // New Professional Tax Invoice Format (Similar to attached image)
  const printBillProfessionalFormat = () => {
    if (!invoiceSummary || invoiceSummary.itemTotal === 0) {
      toast.error('No invoice data available for printing. Please add items first.');
      return;
    }

    const invoiceNumber = invoiceSummary.invoice_number || 'DRAFT';
    const invoiceDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const invoiceTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    // Company details
    const companyName = companyProfile?.name || 'CloudNet Softwares';
    const companyPhone = companyProfile?.phone_number || '+66-948712350';
    const companyEmail = companyProfile?.email || 'info@cloudnetsoftwares.com';
    const companyWebsite = companyProfile?.website || 'www.cloudnetsoftwares.com';
    const companyAddress = companyProfile?.address || '123 Beach Road';
    const companyCity = companyProfile?.city || 'Pattaya';
    const companyState = companyProfile?.state || '';
    const companyZip = companyProfile?.zip_code || '20150';
    const companyCountry = companyProfile?.country || 'Thailand';
    const companyTaxId = companyProfile?.tax_id || 'TAX123456789';
    const companyBranch = companyProfile?.branch_number || '00000';

    // Logo
    const logoUrl = companyProfile?.logo && companyProfile?.logo_type 
      ? blobToBase64(companyProfile.logo, companyProfile.logo_type) 
      : null;

    // Customer details
    const customerName = customerDetails?.name || 'Walk-in Customer';
    const customerPhone = customerDetails?.phone || 'N/A';
    const customerEmail = customerDetails?.email || '';
    const customerAddress = customerDetails?.address || '';
    const customerTaxId = customerDetails?.taxid || '';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${invoiceNumber}</title>
        <style>
          @page { size: A4; margin: 15mm; }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 11px;
            line-height: 1.4;
            color: #333;
            background: white;
          }
          .invoice-container {
            max-width: 210mm;
            min-height: 277mm;
            margin: 0 auto;
            padding: 15px;
            border: 2px solid #2c3e50;
            display: flex;
            flex-direction: column;
          }
          
          /* Header Section */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 3px solid #3498db;
          }
          .header-left {
            flex: 1;
          }
          .header-right {
            text-align: right;
            background: #e8f4f8;
            padding: 15px;
            border-radius: 8px;
            min-width: 250px;
          }
          .company-logo {
            max-width: 150px;
            max-height: 60px;
            margin-bottom: 10px;
          }
          .company-name {
            font-size: 16px;
            font-weight: bold;
            color: #2c3e50;
            margin-bottom: 5px;
          }
          .company-info {
            font-size: 9px;
            color: #555;
            line-height: 1.6;
          }
          .tax-id-branch {
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px solid #ddd;
          }
          .invoice-title {
            font-size: 20px;
            font-weight: bold;
            color: #2c3e50;
            margin-bottom: 8px;
            text-transform: uppercase;
          }
          .invoice-subtitle {
            font-size: 10px;
            color: #666;
            font-style: italic;
          }
          .doc-info {
            margin-top: 10px;
            font-size: 10px;
          }
          .doc-info-row {
            display: flex;
            justify-content: space-between;
            padding: 3px 0;
          }
          .doc-label {
            font-weight: 600;
            color: #555;
          }
          
          /* Customer Section */
          .customer-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 20px;
          }
          .info-box {
            border: 2px solid #e0e0e0;
            border-radius: 8px;
            padding: 12px;
            background: #fafafa;
          }
          .info-box-header {
            font-size: 11px;
            font-weight: bold;
            color: #2c3e50;
            margin-bottom: 10px;
            padding-bottom: 6px;
            border-bottom: 2px solid #3498db;
          }
          .info-row {
            display: flex;
            padding: 4px 0;
            font-size: 10px;
          }
          .info-label {
            font-weight: 600;
            min-width: 100px;
            color: #555;
          }
          .info-value {
            flex: 1;
            color: #333;
          }
          
          /* Items Table */
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 10px;
          }
          .items-table thead {
            background: #2c3e50;
            color: white;
          }
          .items-table th {
            padding: 10px 8px;
            text-align: left;
            font-weight: 600;
            border: 1px solid #34495e;
          }
          .items-table tbody td {
            padding: 8px;
            border: 1px solid #ddd;
            vertical-align: top;
          }
          .items-table tbody tr:nth-child(even) {
            background: #f9f9f9;
          }
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          
          /* Summary Section */
          .summary-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-top: 20px;
          }
          .summary-left {
            flex: 1;
            font-size: 9px;
            color: #666;
            line-height: 1.6;
          }
          .summary-right {
            width: 300px;
            border: 2px solid #2c3e50;
            border-radius: 8px;
            overflow: hidden;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 12px;
            font-size: 11px;
            border-bottom: 1px solid #e0e0e0;
          }
          .summary-row:last-child {
            border-bottom: none;
          }
          .summary-label {
            font-weight: 600;
            color: #555;
          }
          .summary-value {
            font-weight: 500;
            color: #333;
          }
          .vat-row {
            background: #fff9e6;
          }
          .total-row {
            background: #3498db;
            color: white;
            font-size: 14px;
            font-weight: bold;
          }
          
          /* Footer */
          .footer-section {
            margin-top: 30px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            padding-top: 20px;
            border-top: 2px solid #ddd;
          }
          .received-section, .approved-section {
            text-align: center;
            width: 200px;
          }
          .signature-label {
            font-size: 10px;
            font-weight: 600;
            margin-bottom: 50px;
            color: #555;
          }
          .signature-line {
            border-top: 1px solid #333;
            padding-top: 5px;
            font-size: 9px;
          }
          .stamp-placeholder {
            width: 80px;
            height: 80px;
            border: 2px dashed #ccc;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 10px;
            color: #ccc;
            font-size: 9px;
          }
          
          @media print {
            body { margin: 0; padding: 0; }
            .invoice-container { border: 1px solid #2c3e50; }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          
          <!-- Header -->
          <div class="header-wrapper">
            <div class="header-left">
              ${logoUrl ? `<img src="${logoUrl}" alt="Logo" class="company-logo" />` : ''}
              <div class="company-name">${companyName}</div>
              <div class="company-info">
                ${companyAddress}, ${companyCity}, ${companyState} ${companyZip}<br>
                ${companyCountry}
              </div>
              <div class="tax-id-branch">
                <strong>Tax ID:</strong> ${companyTaxId}<br>
                <strong>Branch No:</strong> ${companyBranch}
              </div>
            </div>
            
            <div class="header-right">
              <div class="invoice-title">Delivery Note/Invoice</div>
              <div class="invoice-subtitle">Original (for customer)</div>
              
              <div class="doc-info">
                <div class="doc-info-row">
                  <span class="doc-label">Document No.:</span>
                  <span>${invoiceNumber}</span>
                </div>
                <div class="doc-info-row">
                  <span class="doc-label">Date:</span>
                  <span>${invoiceDate}</span>
                </div>
                <div class="doc-info-row">
                  <span class="doc-label">Credit:</span>
                  <span>30 Days</span>
                </div>
                <div class="doc-info-row">
                  <span class="doc-label">Sales Person:</span>
                  <span>-</span>
                </div>
                <div class="doc-info-row">
                  <span class="doc-label">Salesman:</span>
                  <span>-</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Customer Details -->
          <div class="customer-section">
            <div class="info-box">
              <div class="info-box-header">Customer's name and address</div>
              <div class="info-row">
                <span class="info-label">Name:</span>
                <span class="info-value">${customerName}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Address:</span>
                <span class="info-value">${customerAddress || '-'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Phone:</span>
                <span class="info-value">${customerPhone}</span>
              </div>
              ${customerEmail ? `
              <div class="info-row">
                <span class="info-label">Email:</span>
                <span class="info-value">${customerEmail}</span>
              </div>
              ` : ''}
            </div>
            
            <div class="info-box">
              <div class="info-box-header">Customer's Tax ID and branch number</div>
              <div class="info-row">
                <span class="info-label">Tax ID:</span>
                <span class="info-value">${customerTaxId || '-'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Branch No.:</span>
                <span class="info-value">-</span>
              </div>
              <div class="info-row">
                <span class="info-label">Contact:</span>
                <span class="info-value">${customerPhone}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Phone:</span>
                <span class="info-value">${customerPhone}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Email:</span>
                <span class="info-value">${customerEmail || '-'}</span>
              </div>
            </div>
          </div>
          
          <!-- Items Table -->
          <div style="flex-grow: 1;">
          <table class="items-table">
            <thead>
              <tr>
                <th width="5%" class="text-center">#</th>
                <th width="35%">Description</th>
                <th width="10%" class="text-center">Quantity</th>
                <th width="15%" class="text-right">Unit Price</th>
                <th width="15%" class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${itemRows.length > 0 ? itemRows.map((item, index) => `
                <tr>
                  <td class="text-center">${index + 1}</td>
                  <td>${item.itemName || item.item_name || ''}</td>
                  <td class="text-center">${item.quantity || 0}</td>
                  <td class="text-right">${parseFloat(item.rate || 0).toFixed(2)}</td>
                  <td class="text-right">${parseFloat(item.amount || item.total_price || 0).toFixed(2)}</td>
                </tr>
              `).join('') : '<tr><td colspan="5" class="text-center">No items found</td></tr>'}
            </tbody>
          </table>
          
          </div>
          
          <!-- Summary -->
          <div class="summary-wrapper" style="margin-top: auto;">
            <div class="summary-left">
              <strong>(note hundred thousand baht)</strong>
            </div>
            
            <div class="summary-right">
              <div class="summary-row">
                <span class="summary-label">Total:</span>
                <span class="summary-value">${invoiceSummary.itemTotal.toFixed(2)} THB</span>
              </div>
              ${invoiceSummary.discount > 0 ? `
              <div class="summary-row">
                <span class="summary-label">Discount:</span>
                <span class="summary-value">- ${invoiceSummary.discount.toFixed(2)} THB</span>
              </div>
              ` : ''}
              ${showVatDetails ? `
              <div class="summary-row vat-row">
                <span class="summary-label">VAT 7%:</span>
                <span class="summary-value">${invoiceSummary.tax.toFixed(2)} THB</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">Total Excluding VAT:</span>
                <span class="summary-value">${(invoiceSummary.subTotal - invoiceSummary.tax).toFixed(2)} THB</span>
              </div>
              ` : ''}
              <div class="summary-row total-row">
                <span class="summary-label">Grand Total:</span>
                <span class="summary-value">${invoiceSummary.grandTotal.toFixed(2)} THB</span>
              </div>
            </div>
          </div>
          
          <!-- Footer with Signatures -->
          <div class="footer-section">
            <div class="received-section">
              <div class="signature-label">Received by</div>
              <div class="stamp-placeholder">SAMPLE<br>STAMP</div>
              <div class="signature-line">
                ___________________<br>
                Date: ${invoiceDate}
              </div>
            </div>
            
            <div class="approved-section">
              <div class="signature-label">Approved by</div>
              <div style="height: 60px; margin-bottom: 20px;"></div>
              <div class="signature-line">
                ___________________<br>
                Date: ${invoiceDate}
              </div>
            </div>
          </div>
          
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleCustomerSubmit = () => {
    if (!customerDetails.name || !customerDetails.phone) {
      toast.error('Please fill customer details')
      return
    }
    setShowCustomerModal(false)
    printBill()
  }
  useEffect(() => {
    const handleClickOutside = e => {
      if (!e.target.closest('tr')) {
        setEditableRowIndex(null)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const [showInvoicePreview, setShowInvoicePreview] = useState(false)
  const [showVATInvoicePreview, setShowVATInvoicePreview] = useState(false)



  // Helper to build invoice props from current bill, dynamic by taxType
  async function getInvoiceProps(itemId = null) {
    let invId = itemId || window.lastSavedBillId || '';
    let myfinalbilldata = [];
    let myOrderItemsData = [];
    let myCustomerdetails = {};
    // Fetch from DB if invId is present
    if (invId) {
      myfinalbilldata = await fetchData("final_bill", () => { }, "id", { id: invId });
      // Dynamic table based on taxType
      const orderTable = taxType === 'gst' ? 'order_items_gst' : 'order_items';
      myOrderItemsData = await fetchData(orderTable, () => { }, "id", { order_id: invId });
      if (myfinalbilldata && myfinalbilldata[0] && myfinalbilldata[0].customer_id) {
        const custArr = await fetchData("customers", () => { }, "id", { id: myfinalbilldata[0].customer_id });
        myCustomerdetails = custArr && custArr[0] ? custArr[0] : {};
      }
    }
    // Company
    const company = companyDetails;
    // Items
    const items = (myOrderItemsData.length > 0 ? myOrderItemsData : itemRows).map(row => {
      if (taxType === 'gst') {
        return {
          item_name: (row && (row.itemName || row.item_name)) || '',
          description: (row && row.description) || '',
          quantity: row ? Number(row.quantity) : 0,
          rate: row ? Number(row.rate) : 0,
          cgst: row ? Number(row.cgst) : 0,
          sgst: row ? Number(row.sgst) : 0,
          igst: row ? Number(row.igst) : 0,
          amount: row ? Number(row.amount || row.total_price) : 0,
          discountPercent: row ? Number(row.discountPercent) : 0,
          discountValue: row ? Number(row.discountValue) : 0,
          netAmount: row ? Number(row.netAmount) : 0,
          total_price: row ? Number(row.amount || row.total_price) : 0
        };
      } else {
        return {
          item_name: (row && (row.itemName || row.item_name)) || '',
          description: (row && row.description) || '',
          quantity: row ? Number(row.quantity) : 0,
          rate: row ? Number(row.rate) : 0,
          vat: row ? Number(row.vat || 0) : 0,
          amount: row ? Number(row.amount || row.total_price) : 0,
          discountPercent: row ? Number(row.discountPercent) : 0,
          discountValue: row ? Number(row.discountValue) : 0,
          netAmount: row ? Number(row.netAmount) : 0,
          total_price: row ? Number(row.amount || row.total_price) : 0
        };
      }
    });
    // Customer: Only use DB data for print preview, never fall back to UI state
    let customerData = {
      name: '',
      phone: '',
      email: '',
      address: '',
      gst: '',
      vat: '',
      deliveryPlace: ''
    };
    if (myCustomerdetails && typeof myCustomerdetails === 'object' && Object.keys(myCustomerdetails).length > 0) {
      customerData = {
        name: myCustomerdetails.name || '',
        phone: myCustomerdetails.phone || '',
        email: myCustomerdetails.email || '',
        address: myCustomerdetails.address || '',
        gst: myCustomerdetails.gst || '',
        vat: myCustomerdetails.vat || '',
        deliveryPlace: myCustomerdetails.deliveryPlace || ''
      };
    } else if (invId) {
      toast.error('Customer data not found for this bill.');
    }
    // Summary
    const summary = myfinalbilldata && myfinalbilldata[0] ? {
      subtotal: Number(myfinalbilldata[0].subtotal) || 0,
      discount: Number(myfinalbilldata[0].discount_amount) || 0,
      subtotalAfterDiscount: Number(myfinalbilldata[0].subtotal_afterdiscount) || 0,
      tax: Number(myfinalbilldata[0].tax) || 0,
      payment_mode: Number(myfinalbilldata[0].payment_mode) || 0,
      roundoff: Number(myfinalbilldata[0].roundoff) || 0,
      grandTotal: Number(myfinalbilldata[0].grand_total) || 0
    } : {
      subtotal: invoiceSummary.itemTotal,
      discount: invoiceSummary.discount,
      subtotalAfterDiscount: invoiceSummary.subTotal,
      tax: invoiceSummary.tax,
      payment_mode: invoiceSummary.payment_mode,
    
      roundoff: invoiceSummary.roundOff,
      grandTotal: invoiceSummary.grandTotal
    };
    // Taxes
    let taxes = {};
    if (taxType === 'gst') {
      let cgstPercent = 0, sgstPercent = 0, igstPercent = 0;
      let cgstTotal = 0, sgstTotal = 0, igstTotal = 0;
      let cgstCount = 0, sgstCount = 0, igstCount = 0;
      (myOrderItemsData.length > 0 ? myOrderItemsData : itemRows).forEach(item => {
        if (parseFloat(item.cgst || 0) > 0) { cgstPercent += parseFloat(item.cgst || 0); cgstCount++; }
        if (parseFloat(item.sgst || 0) > 0) { sgstPercent += parseFloat(item.sgst || 0); sgstCount++; }
        if (parseFloat(item.igst || 0) > 0) { igstPercent += parseFloat(item.igst || 0); igstCount++; }
        cgstTotal += (Number(item.amount || item.total_price) - Number(item.discountValue || 0)) * (parseFloat(item.cgst || 0) / 100);
        sgstTotal += (Number(item.amount || item.total_price) - Number(item.discountValue || 0)) * (parseFloat(item.sgst || 0) / 100);
        igstTotal += (Number(item.amount || item.total_price) - Number(item.discountValue || 0)) * (parseFloat(item.igst || 0) / 100);
      });
      cgstPercent = cgstCount ? cgstPercent / cgstCount : 0;
      sgstPercent = sgstCount ? sgstPercent / sgstCount : 0;
      igstPercent = igstCount ? igstPercent / igstCount : 0;
      taxes = {
        cgstPercent: cgstPercent.toFixed(2),
        sgstPercent: sgstPercent.toFixed(2),
        igstPercent: igstPercent.toFixed(2),
        cgstTotal: cgstTotal.toFixed(2),
        sgstTotal: sgstTotal.toFixed(2),
        igstTotal: igstTotal.toFixed(2)
      };
    } else {
      let vatPercent = 0, vatTotal = 0, vatCount = 0;
      (myOrderItemsData.length > 0 ? myOrderItemsData : itemRows).forEach(item => {
        if (parseFloat(item.vat || 0) > 0) { vatPercent += parseFloat(item.vat || 0); vatCount++; }
        vatTotal += (Number(item.amount || item.total_price) - Number(item.discountValue || 0)) * (parseFloat(item.vat || 0) / 100);
      });
      vatPercent = vatCount ? vatPercent / vatCount : 0;
      taxes = {
        vatPercent: vatPercent.toFixed(2),
        vatTotal: vatTotal.toFixed(2)
      };
    }
    // Invoice meta
    const invoiceNo = invId;
    const invoiceDate = myfinalbilldata && myfinalbilldata[0] ? myfinalbilldata[0].inv_date : new Date().toLocaleDateString();
    const invoiceTime = myfinalbilldata && myfinalbilldata[0] ? myfinalbilldata[0].inv_time : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return {
      company,
      customer: customerData,
      items,
      summary,
      taxes,
      invoiceNo,
      invoiceDate,
      invoiceTime
    };
  }



  // Modal state for Add New Customer
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    contact: '',
    email: '',
    taxid: '',
    address: ''
  });
  const handleNewCustomerInput = e => {
    const { name, value } = e.target;
    setNewCustomer(prev => ({ ...prev, [name]: value }));
  };
  const handleAddCustomer = async e => {
    e.preventDefault();
    try {
      await axios.post(
        '/insertdata/customers',
        {
          name: newCustomer.name,
          contact: newCustomer.contact,
          email: newCustomer.email,
          taxid: newCustomer.taxid,
          address: newCustomer.address
        },
        getHeaders()
      );
      toast.success('Customer added successfully!');
      setShowAddCustomerModal(false);
      setNewCustomer({ name: '', contact: '', email: '', taxid: '', address: '' });
      // Optionally refresh customer list
      fetchData('customers', setAllCustomers, 'id', {});
    } catch (err) {
      toast.error('Error in adding customer');
    }
  };

  // Show password modal for edit
  const promptEditPassword = (invoiceNo) => {
    if (!invoiceNo.trim()) {
      toast.error('Please enter invoice number');
      return;
    }
    // Directly load bill without password verification
    loadBillForEdit(invoiceNo);
    
    // Commented out password modal
    // setPendingEditInvoiceNo(invoiceNo);
    // setEditPassword('');
    // setShowPasswordModal(true);
  };

  // Verify password and load bill
  const verifyPasswordAndLoadBill = () => {
    const correctPassword = 'Acceess@112';
    
    if (editPassword === correctPassword) {
      setShowPasswordModal(false);
      setEditPassword('');
      loadBillForEdit(pendingEditInvoiceNo);
    } else {
      toast.error('Incorrect password! Access denied.');
      setEditPassword('');
    }
  };

  // Load bill for editing
  const loadBillForEdit = async (invoiceNo) => {
    try {
      if (!invoiceNo.trim()) {
        toast.error('Please enter invoice number');
        return;
      }

      // Fetch bill data
      const billResponse = await fetchData("final_bill", () => {}, "id", { id: invoiceNo.trim() });
      if (!billResponse || billResponse.length === 0) {
        toast.error('Invoice not found');
        return;
      }

      const billData = billResponse[0];

      // Fetch order items
      const itemsResponse = await fetchData("order_items", () => {}, "order_id", { order_id: invoiceNo.trim() });
      if (!itemsResponse || itemsResponse.length === 0) {
        toast.error('No items found for this invoice');
        return;
      }

      // Set edit mode
      setIsEditMode(true);
      setEditBillId(billData.id);

      // Load customer details
      setCustomerDetails({
        name: billData.customer_name || '',
        phone: billData.customer_phone || ''
      });

      setCustomer({
        name: billData.customer_name || '',
        phone: billData.customer_phone || '',
        email: billData.customer_email || '',
        address: billData.customer_address || '',
        gst: billData.customer_gst || '',
        deliveryPlace: ''
      });

      // Set payment details
      setPaymentType(billData.payment_mode || 'Cash');
      setDiscountType(billData.discount_type || 'percentage');
      setDiscountValue(parseFloat(billData.discount_value) || 0);

      // Load items into table
      const loadedRows = itemsResponse.map((item, index) => ({
        id: index + 1,
        itemName: item.item_name || '',
        description: item.description || '',
        quantity: parseFloat(item.quantity) || 0,
        rate: parseFloat(item.rate) || 0,
        amount: (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0),
        discountPercent: parseFloat(item.discount_percent) || 0,
        discountValue: ((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0) * (parseFloat(item.discount_percent) || 0)) / 100,
        vat: parseFloat(item.vat) || 7,
        cgst: parseFloat(item.cgst) || 0,
        sgst: parseFloat(item.sgst) || 0,
        igst: parseFloat(item.igst) || 0,
        netAmount: parseFloat(item.total_price) || 0,
        taxIncluded: false
      }));

      setItemRows(loadedRows);
      toast.success(`Bill #${invoiceNo} loaded for editing`);
      
    } catch (error) {
      console.error('Error loading bill:', error);
      toast.error('Failed to load bill for editing');
    }
  };

  // Save edited bill
  const saveEditedBill = async () => {
    try {
      if (!editBillId) {
        toast.error('No bill selected for editing');
        return;
      }

      if (itemRows.length === 0) {
        toast.error('Please add at least one item');
        return;
      }

      const finalTotal = calculateFinalTotal();

      const billData = {
        customer_id: customer.custid || null,
        customer_name: customer.name || customerDetails.name || 'Walk-in Customer',
        tablenumber: 0,
        subtotal: invoiceSummary.itemTotal,
        subtotal_afterdiscount: invoiceSummary.subTotal,
        tax: invoiceSummary.tax,
        discount_type: discountType,
        discount_value: discountValue,
        round_off: invoiceSummary.roundOff,
        grand_total: invoiceSummary.grandTotal,
        payment_mode: paymentType,
        status: 1,
        setup_date: new Date().toISOString().split('T')[0],
        bank_account_id: null,
        reference_number: null,
        remarks: null,
        order_items: itemRows.map(row => ({
          table_number: 0,
          item_name: row.itemName,
          description: row.description || '',
          quantity: row.quantity,
          rate: row.rate,
          discount_percent: row.discountPercent,
          vat: row.vat || 7,
          cgst: row.cgst || 0,
          sgst: row.sgst || 0,
          igst: row.igst || 0,
          total_price: row.netAmount,
          invoice_number: null,
          status: 1,
          setup_date: new Date().toISOString().split('T')[0],
          table_cat_id: null
        }))
      };

      const response = await axios.put(
        `/editbill/${editBillId}`,
        billData,
        getHeaders()
      );

      if (response.data.success) {
        toast.success(`Bill #${editBillId} updated successfully!`);
        
        // Reset edit mode
        setIsEditMode(false);
        setEditBillId(null);
        setEditInvoiceNo('');
        
        // Clear form
        setItemRows([]);
        setCustomer({ name: '', phone: '', email: '', address: '', gst: '', deliveryPlace: '' });
        setCustomerDetails({ name: '', phone: '' });
        setDiscountValue(0);
        
        // Optionally print updated bill
        if (window.confirm('Bill updated successfully! Do you want to print?')) {
          printBillVATFormat(editBillId);
        }
      } else {
        throw new Error(response.data.message || 'Failed to update bill');
      }

    } catch (error) {
      console.error('Error updating bill:', error);
      toast.error(error.response?.data?.message || 'Failed to update bill');
    }
  };

  // Cancel edit mode
  const cancelEditMode = () => {
    if (window.confirm('Cancel editing? All changes will be lost.')) {
      setIsEditMode(false);
      setEditBillId(null);
      setEditInvoiceNo('');
      setItemRows([]);
      setCustomer({ name: '', phone: '', email: '', address: '', gst: '', deliveryPlace: '' });
      setCustomerDetails({ name: '', phone: '' });
      setDiscountValue(0);
      toast.info('Edit cancelled');
    }
  };

  const renderContent = () => (
    <>
      <ToastContainer />
      
      {/* Professional Styling */}
      <style jsx>{`
        .professional-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          border: 1px solid #e2e8f0;
          margin-bottom: 24px;
          overflow: hidden;
          transition: all 0.3s ease;
        }
        
        .professional-card:hover {
          box-shadow: 0 8px 32px rgba(0,0,0,0.12);
          transform: translateY(-2px);
        }
        
        .table-modern {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
        
        .table-modern th {
          background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
          color: white;
          font-weight: 600;
          padding: 12px 8px;
          font-size: 11px;
          border: none;
        }
        
        .table-modern tbody tr {
          transition: all 0.2s ease;
        }
        
        .table-modern tbody tr:hover {
          background-color: #f8fafc;
          transform: scale(1.01);
        }
        
        .table-modern tbody td {
          padding: 10px 8px;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: middle;
        }
        
        .form-control-modern {
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          padding: 8px 12px;
          transition: all 0.3s ease;
          background: #fafbfc;
        }
        
        .form-control-modern:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          background: white;
        }
        
        .btn-modern {
          border-radius: 8px;
          padding: 8px 16px;
          font-weight: 500;
          transition: all 0.3s ease;
          border: none;
        }
        
        .btn-modern:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }
      `}</style>
      
      {/* Dashboard Navigation Button - Only for cashiers */}
      {isCashier && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1000 }}>
          <button
            className="btn btn-primary"
            onClick={navigateToDashboard}
            style={{
              padding: '10px 15px',
              fontSize: '14px',
              borderRadius: '25px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
            }}
          >
            🏠 Dashboard
          </button>
        </div>
      )}

      <div className='row mb-4'>
        {/* <div className="col-md-6">
          <CardComponent>


          </CardComponent>
        </div> */}
        {/* Customer Details & Scan Code - Two Column Layout */}
        <div className="row mb-4" style={{ margin: '0 -12px' }}>
          {/* Customer Details Section */}
          <div className="col-lg-6 mb-3" style={{ padding: '0 12px' }}>
            <div 
              style={{ 
                background: 'white',
                borderRadius: '10px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                height: 'fit-content'
              }}
            >
              <div 
                className="card-header" 
                style={{ 
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '12px 18px',
                  borderBottom: 'none',
                  minHeight: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div className="d-flex align-items-center">
                  <i className="fas fa-users me-2" style={{ fontSize: '1rem' }}></i>
                  <span style={{ 
                    fontSize: '0.95rem', 
                    fontWeight: '600', 
                    letterSpacing: '0.2px'
                  }}>Customer Details</span>
                </div>
                <button 
                  type="button"
                  className="btn btn-sm p-1"
                  onClick={() => setShowCustomerDetails(!showCustomerDetails)}
                  style={{ 
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    transition: 'all 0.3s ease',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className={`fas ${showCustomerDetails ? 'fa-chevron-up' : 'fa-chevron-down'}`} style={{ fontSize: '0.8rem' }}></i>
                </button>
              </div>
              {showCustomerDetails && (
                <div className="p-3" style={{ background: '#fafbfc', margin: '12px', borderRadius: '6px' }}>
                  <div className='row' style={{ margin: '0 -6px' }}>
                    {/* Name */}
                    <div className='col-12 mb-2' style={{ padding: '0 6px' }}>
                      <label
                        htmlFor='customerName'
                        className='form-label fw-semibold'
                        style={{ fontSize: '11px', marginBottom: '3px' }}
                      >
                        Customer Name
                      </label>
                      <Select
                        id='customerName'
                        name='customerName'
                        value={
                          customer.name
                            ? { label: customer.name, value: customer.name }
                            : null
                        }
                        onInputChange={(inputValue, { action }) => {
                          if (action === 'input-change') {
                            setCustomer({ ...customer, name: inputValue })
                          }
                        }}
                        onChange={selectedOption => {
                          if (!selectedOption) return
                          // Find customer by name from allCustomers
                          const selectedCustomer = allCustomers.find(
                            c => c.name === selectedOption.value
                          )
                          if (selectedCustomer) {
                            setCustomer({
                              name: selectedCustomer.name || '',
                              phone: selectedCustomer.contact || '',
                              email: selectedCustomer.email || '',
                              address: selectedCustomer.address || '',
                              gst: selectedCustomer.taxid || '',
                              deliveryPlace: selectedCustomer.deliveryPlace || '',
                              custid: selectedCustomer.id || ''
                            })
                          } else {
                            setCustomer({ ...customer, name: selectedOption.value })
                          }
                        }}
                        options={allCustomers.map(c => ({
                          label: c.name,
                          value: c.name
                        }))}
                        placeholder='Type or select customer name'
                        isClearable
                        openMenuOnClick={true}
                        menuPlacement='auto'
                        menuPortalTarget={
                          typeof window !== 'undefined' ? document.body : null
                        }
                        styles={{ 
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: base => ({ ...base, fontSize: '11px', minHeight: '28px', padding: '2px 4px' }),
                          option: base => ({ ...base, fontSize: '11px', padding: '4px 8px' }),
                          placeholder: base => ({ ...base, fontSize: '11px' }),
                          singleValue: base => ({ ...base, fontSize: '11px' })
                        }}
                        filterOption={(option, inputValue) =>
                          option.label
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        }
                      />
                    </div>

                    {/* Phone */}
                    <div className='col-md-6 mb-2' style={{ padding: '0 6px' }}>
                      <label
                        htmlFor='customerPhone'
                        className='form-label fw-semibold'
                        style={{ fontSize: '11px', marginBottom: '3px' }}
                      >
                        Phone Number
                      </label>
                      <input
                        id='customerPhone'
                        name='customerPhone'
                        value={customer.phone}
                        onChange={e =>
                          setCustomer({ ...customer, phone: e.target.value })
                        }
                        type='tel'
                        className='form-control'
                        placeholder='Enter phone number'
                        style={{ fontSize: '11px', padding: '4px 8px', height: '28px' }}
                      />
                    </div>

                    {/* Email */}
                    <div className='col-md-6 mb-2' style={{ padding: '0 6px' }}>
                      <label
                        htmlFor='customerEmail'
                        className='form-label fw-semibold'
                        style={{ fontSize: '11px', marginBottom: '3px' }}
                      >
                        Email
                      </label>
                      <input
                        id='customerEmail'
                        name='customerEmail'
                        value={customer.email}
                        onChange={e =>
                          setCustomer({ ...customer, email: e.target.value })
                        }
                        type='email'
                        className='form-control'
                        placeholder='Enter email address'
                        style={{ fontSize: '11px', padding: '4px 8px', height: '28px' }}
                      />
                    </div>

                    {/* Address */}
                    <div className='col-12 mb-2' style={{ padding: '0 6px' }}>
                      <label
                        htmlFor='customerAddress'
                        className='form-label fw-semibold'
                        style={{ fontSize: '11px', marginBottom: '3px' }}
                      >
                        Address
                      </label>
                      <input
                        id='customerAddress'
                        name='customerAddress'
                        value={customer.address}
                        onChange={e =>
                          setCustomer({ ...customer, address: e.target.value })
                        }
                        type='text'
                        className='form-control'
                        placeholder='Enter address'
                        style={{ fontSize: '11px', padding: '4px 8px', height: '28px' }}
                      />
                    </div>

                    {/* GST No */}
                    <div className='col-md-6 mb-2' style={{ padding: '0 6px' }}>
                      <label
                        htmlFor='customerGST'
                        className='form-label fw-semibold'
                        style={{ fontSize: '11px', marginBottom: '3px' }}
                      >
                        GST Number (Optional)
                      </label>
                      <input
                        id='customerGST'
                        name='customerGST'
                        value={customer.gst}
                        onChange={e =>
                          setCustomer({ ...customer, gst: e.target.value })
                        }
                        type='text'
                        className='form-control'
                        placeholder='Enter GST number'
                        style={{ fontSize: '11px', padding: '4px 8px', height: '28px' }}
                      />
                    </div>

                    {/* Place of Delivery */}
                    <div className='col-md-6 mb-2' style={{ padding: '0 6px' }}>
                      <label
                        htmlFor='customerDeliveryPlace'
                        className='form-label fw-semibold'
                        style={{ fontSize: '11px', marginBottom: '3px' }}
                      >
                        Place of Delivery
                      </label>
                      <input
                        id='customerDeliveryPlace'
                        name='customerDeliveryPlace'
                        value={customer.deliveryPlace}
                        onChange={e =>
                          setCustomer({
                            ...customer,
                            deliveryPlace: e.target.value
                          })
                        }
                        type='text'
                        className='form-control'
                        placeholder='Enter delivery place'
                        style={{ fontSize: '11px', padding: '4px 8px', height: '28px' }}
                      />
                    </div>
                    
                    <div className='col-12 mt-1' style={{ padding: '0 6px' }}>
                      <button 
                        className='btn btn-outline-primary btn-sm' 
                        onClick={() => setShowAddCustomerModal(true)}
                        style={{ fontSize: '11px', padding: '4px 10px', height: '28px' }}
                      >
                        <i className="fas fa-plus me-1" style={{ fontSize: '9px' }}></i>
                        Add New Customer
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Scan / Enter Item Code Section */}
          <div className="col-lg-6 mb-3" style={{ padding: '0 12px' }}>
            <div 
              style={{ 
                background: 'white',
                borderRadius: '10px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                height: 'fit-content'
              }}
            >
              <div 
                className="card-header" 
                style={{ 
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: 'white',
                  padding: '12px 18px',
                  borderBottom: 'none',
                  minHeight: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div className="d-flex align-items-center">
                  <i className="fas fa-barcode me-2" style={{ fontSize: '1rem' }}></i>
                  <span style={{ 
                    fontSize: '0.95rem', 
                    fontWeight: '600', 
                    letterSpacing: '0.2px'
                  }}>Scan / Enter Item Code</span>
                </div>
                <button 
                  type="button"
                  className="btn btn-sm p-1"
                  onClick={() => setShowScanCode(!showScanCode)}
                  style={{ 
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    transition: 'all 0.3s ease',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className={`fas ${showScanCode ? 'fa-chevron-up' : 'fa-chevron-down'}`} style={{ fontSize: '0.8rem' }}></i>
                </button>
              </div>
              {showScanCode && (
                <div className="p-3" style={{ background: '#fafbfc', margin: '12px', borderRadius: '6px' }}>
                  <div className='row mb-2' style={{ margin: '0 -6px' }}>
                    <div className='col-12 mb-3' style={{ padding: '0 6px' }}>
                      <label style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px', display: 'block', color: '#374151' }}>
                        <i className="fas fa-hashtag me-1"></i>Enter Item Code
                      </label>
                      <form onSubmit={handleScan}>
                        <Textfield
                          className='form-control'
                          id='itemCodeInput'
                          value={barcode}
                          onChange={e => setBarcode(e.target.value)}
                          type='text'
                          name='itemCode'
                          placeholder='Enter item code manually'
                          style={{ fontSize: '12px', padding: '6px 10px' }}
                        />
                      </form>
                    </div>
                    <div className='col-12 mb-3' style={{ padding: '0 6px' }}>
                      <label style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px', display: 'block', color: '#374151' }}>
                        <i className="fas fa-barcode me-1"></i>Scan Barcode
                      </label>
                      <form onSubmit={handleScan}>
                        <Textfield
                          className='form-control'
                          id='barcodeInput'
                          value={barcode}
                          onChange={e => setBarcode(e.target.value)}
                          type='text'
                          name='barcode'
                          placeholder='Scan barcode here'
                          style={{ fontSize: '12px', padding: '6px 10px' }}
                          autoFocus
                        />
                      </form>
                    </div>
                    <div className='col-12' style={{ padding: '0 6px' }}>
                      <button 
                        className='btn btn-primary w-100' 
                        type='button' 
                        onClick={handleScan} 
                        style={{ padding: '8px 12px', fontSize: '12px' }}
                      >
                        <i className="fas fa-search me-2"></i>Search Item
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div 
          style={{ 
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            marginBottom: '24px'
          }}
        >
          <div 
            className="card-header" 
            style={{ 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '16px 24px',
              borderBottom: 'none'
            }}
          >
            <div className="d-flex align-items-center">
              <i className="fas fa-plus-circle me-3" style={{ fontSize: '1.2rem' }}></i>
              <span style={{ fontSize: '1.1rem', fontWeight: '600', letterSpacing: '0.3px' }}>Add Items</span>
            </div>
          </div>
          <div className="p-3">
            <table className='table table-borderless' style={{ fontSize: '12px' }}>
              <thead className='table-dark'>
                <tr style={{ fontSize: '12px' }}>
                  <th style={{ fontSize: '11px' }}>S.No</th>
                  <th style={{ fontSize: '11px' }}>Item Name</th>
                  <th style={{ fontSize: '11px' }}>Description</th>
                  <th style={{ fontSize: '11px' }}>Rate</th>
                  <th style={{ fontSize: '11px' }}>Qty</th>
                  <th style={{ fontSize: '11px' }}>Amount</th>
                  <th style={{ fontSize: '11px' }}>Disc%</th>
                  <th style={{ fontSize: '11px' }}>Disc Value</th>
                  {taxType === 'vat' ? (
                    <th style={{ fontSize: '11px' }}>VAT%</th>
                  ) : (
                    <>
                      <th style={{ fontSize: '11px' }}>CGST%</th>
                      <th style={{ fontSize: '11px' }}>SGST%</th>
                      <th style={{ fontSize: '11px' }}>IGST%</th>
                    </>
                  )}
                  <th style={{ fontSize: '11px' }}>Net Amount</th>
                  <th style={{ fontSize: '11px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {itemRows.map((row, index) => (
                  <tr key={row.id} style={{ cursor: 'pointer', fontSize: '12px' }}>
                    <td>{index + 1}</td>

                    {/* Item Name */}
                    <td style={{ position: 'relative', minWidth: 220 }}>
                      {editableRowIndex === index ? (
                        <Select
                          id={`itemName-${index}`}
                          ref={el => (itemNameRefs.current[index] = el)}
                          value={
                            row.itemName
                              ? { label: row.itemName, value: row.itemName }
                              : null
                          }
                          onInputChange={(inputValue, { action }) => {
                            if (action === 'input-change')
                              handleRowChange(index, 'itemName', inputValue)
                          }}
                          onChange={(selectedOption, { action }) => {
                            if (!selectedOption) return
                            const selectedItem = allItems.find(
                              item => item.iname === selectedOption.value
                            )
                            if (selectedItem) {
                              handleItemSelect(index, selectedItem)
                              // Focus description field after selection (arrow+enter or mouse)
                              setTimeout(() => {
                                const descInput = document.getElementById(
                                  `description-${index}`
                                )
                                if (descInput) descInput.focus()
                              }, 0)
                            }
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              // Only add new row if not selecting from menu
                              if (!row.itemName) return
                              // If menu is open and an option is highlighted, let react-select handle it
                              // Otherwise, add new row and focus
                              setTimeout(() => {
                                if (
                                  document.activeElement &&
                                  document.activeElement.getAttribute('id') ===
                                  `itemName-${index}`
                                ) {
                                  addNewRow(true)
                                }
                              }, 0)
                            }
                          }}
                          options={allItems.map(item => ({
                            label: item.iname,
                            value: item.iname
                          }))}
                          placeholder='Type or select item name'
                          isClearable
                          openMenuOnClick={true}
                          menuPlacement='auto'
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                          filterOption={(option, inputValue) =>
                            option.label
                              .toLowerCase()
                              .includes(inputValue.toLowerCase())
                          }
                        />
                      ) : (
                        <span>
                          {row.itemName || (
                            <span className='text-muted'>Click to edit</span>
                          )}
                        </span>
                      )}
                    </td>

                    {/* Description */}
                    <td>
                      {editableRowIndex === index ? (
                        <Textfield
                          id={`description-${index}`}
                          value={row.description}
                          onChange={e =>
                            handleRowChange(
                              index,
                              'description',
                              e.target.value
                            )
                          }
                          type='text'
                          name='description'
                        />
                      ) : (
                        row.description
                      )}
                    </td>

                    {/* Rate */}
                    <td>
                      {editableRowIndex === index ? (
                        <Textfield
                          id={`rate-${index}`}
                          value={row.rate}
                          onChange={e =>
                            handleRowChange(
                              index,
                              'rate',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          type='number'
                          name='rate'
                        />
                      ) : (
                        row.rate.toFixed(2)
                      )}
                    </td>

                    {/* Qty */}
                    <td>
                      {editableRowIndex === index ? (
                        <Textfield
                          id={`qty-${index}`}
                          value={row.quantity}
                          onChange={e =>
                            handleRowChange(
                              index,
                              'quantity',
                              parseInt(e.target.value) || 1
                            )
                          }
                          type='number'
                          name='quantity'
                        />
                      ) : (
                        row.quantity
                      )}
                    </td>

                    {/* Amount */}
                    <td>{row.amount.toFixed(2)}</td>

                    {/* Disc% */}
                    <td>
                      {editableRowIndex === index ? (
                        <Textfield
                          id={`disc-${index}`}
                          value={row.discountPercent}
                          onChange={e =>
                            handleRowChange(
                              index,
                              'discountPercent',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          type='number'
                          name='discountPercent'
                        />
                      ) : (
                        row.discountPercent
                      )}
                    </td>

                    {/* Disc Value */}
                    <td>{row.discountValue.toFixed(2)}</td>


                    {/* VAT% or GST fields */}
                    {taxType === 'vat' ? (
                      <td>
                        {editableRowIndex === index ? (
                          <Textfield
                            id={`vat-${index}`}
                            value={row.tax}
                            onChange={e =>
                              handleRowChange(
                                index,
                                'vat',
                                parseFloat(e.target.value) || 0
                              )
                            }
                            type='number'
                            name='vat'
                          />
                        ) : (
                          row.tax
                        )}
                      </td>
                    ) : (
                      <>
                        <td>
                          {editableRowIndex === index ? (
                            <Textfield
                              id={`cgst-${index}`}
                              value={row.cgst}
                              onChange={e =>
                                handleRowChange(
                                  index,
                                  'cgst',
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              type='number'
                              name='cgst'
                            />
                          ) : (
                            row.cgst
                          )}
                        </td>
                        <td>
                          {editableRowIndex === index ? (
                            <Textfield
                              id={`sgst-${index}`}
                              value={row.sgst}
                              onChange={e =>
                                handleRowChange(
                                  index,
                                  'sgst',
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              type='number'
                              name='sgst'
                            />
                          ) : (
                            row.sgst
                          )}
                        </td>
                        <td>
                          {editableRowIndex === index ? (
                            <Textfield
                              id={`igst-${index}`}
                              value={row.igst}
                              onChange={e =>
                                handleRowChange(
                                  index,
                                  'igst',
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              type='number'
                              name='igst'
                            />
                          ) : (
                            row.igst
                          )}
                        </td>
                      </>
                    )}

                    {/* Net Amount */}
                    <td>
                      {row.netAmount.toFixed(2)}
                      <br />
                      <small className={row.taxIncluded ? 'text-success' : 'text-danger'}>
                        {row.taxIncluded ? 'Tax Included' : 'Tax Excluded'}
                      </small>
                    </td>

                    {/* Delete Button */}
                    <td>
                      <button
                        className='btn btn-danger btn-sm'
                        onClick={e => {
                          e.stopPropagation() // prevent triggering row click
                          deleteRow(index)
                        }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className='btn btn-primary mt-2' onClick={addNewRow}>
              ➕ Add Row
            </button>
          </div>
        </div>
        <div className='row mb-4'>
          <div className='col-md-6'>
            <CardComponent>
              {/* Payment Mode and Grand Total aligned in one row */}
              <div className='d-flex align-items-center justify-content-between flex-wrap gap-2' style={{ minHeight: 60 ,marginTop: '14px' }}>
                <div className='d-flex flex-column' style={{ minWidth: 220 }}>
                  <label className='fw-semibold mb-1'>Payment Mode:</label>
                  <select
                    className='form-control'
                    value={paymentType}
                    onChange={e => setPaymentType(e.target.value)}
                    style={{ maxWidth: 200 }}
                  >
                    <option value='Cash'>Cash</option>
                    <option value='Credit'>Credit</option>
                    <option value='Bank Transfer'>Bank Transfer</option>
                    <option value='Entertainment'>Entertainment</option>
                    <option value='UPI'>UPI</option>
                    <option value='QR Code'>QR Code</option>
                  </select>
                  {paymentType === 'Bank Transfer' && (
                    <div className='mt-2'>
                      <label className='fw-semibold mb-1' style={{ fontSize: '12px' }}>Bank Account:</label>
                      <Select
                        id='bankAccountSelect'
                        value={
                          bankAccountId
                            ? bankAccounts.find(acc => acc.id === bankAccountId)
                              ? { label: bankAccounts.find(acc => acc.id === bankAccountId)?.account_name, value: bankAccountId }
                              : null
                            : null
                        }
                        onChange={selectedOption => {
                          setBankAccountId(selectedOption ? selectedOption.value : null)
                        }}
                        options={bankAccounts.map(acc => ({
                          label: `${acc.account_name} (${acc.account_type})`,
                          value: acc.id
                        }))}
                        placeholder='Select bank account'
                        isClearable
                        isSearchable
                        openMenuOnClick={true}
                        menuPlacement='auto'
                        menuPortalTarget={
                          typeof window !== 'undefined' ? document.body : null
                        }
                        styles={{ 
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: base => ({ ...base, fontSize: '11px', minHeight: '28px', padding: '2px 4px' }),
                          option: base => ({ ...base, fontSize: '11px', padding: '4px 8px' }),
                          placeholder: base => ({ ...base, fontSize: '11px' }),
                          singleValue: base => ({ ...base, fontSize: '11px' })
                        }}
                      />
                    </div>
                  )}
                  {paymentType !== 'Credit' && (
                    <div className='mt-2'>
                      <label className='fw-semibold mb-1' style={{ fontSize: '12px' }}>Reference Number (Optional):</label>
                      <input
                        type='text'
                        className='form-control'
                        value={referenceNumber}
                        onChange={e => setReferenceNumber(e.target.value)}
                        placeholder='Enter reference number'
                        style={{ fontSize: '11px', padding: '4px 8px', height: '28px' }}
                      />
                    </div>
                  )}
                  {paymentType !== 'Credit' && (
                    <div className='mt-2'>
                      <label className='fw-semibold mb-1' style={{ fontSize: '12px' }}>Remarks (Optional):</label>
                      <textarea
                        className='form-control'
                        value={remarks}
                        onChange={e => setRemarks(e.target.value)}
                        placeholder='Enter any remarks'
                        style={{ fontSize: '11px', padding: '4px 8px', minHeight: '50px', resize: 'vertical' }}
                      />
                    </div>
                  )}
                  <div className='form-check mt-3'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='showVatDetails'
                      checked={showVatDetails}
                      onChange={e => setShowVatDetails(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className='form-check-label fw-semibold' htmlFor='showVatDetails' style={{ cursor: 'pointer' }}>
                      Show VAT/Tax Details in Invoice
                    </label>
                  </div>
                </div>
                <div className='text-right' style={{ minWidth: 200 }}>
                  <h5 className='mb-0'>
                    Grand Total: <strong>฿ {invoiceSummary.grandTotal.toFixed(2)}</strong>
                  </h5>
                  <small className='text-muted'>
                    {getAmountInWords(invoiceSummary.grandTotal)}
                  </small>
                </div>
              </div>
            </CardComponent>
          </div>
          <div className='col-md-6'>
            <CardComponent
              title='Invoice Summary'
              headerColor='success'
              customHeader={
                <div className='d-flex justify-content-between align-items-center'>
                  <h5 className='m-0 font-weight-bold'>Invoice Summary</h5>
                  <i className='fas fa-balance-scale fa-2x text-gray-600'></i>
                </div>
              }
            >
              {/* Invoice Date */}
              {/* Financial Summary */}
              <div className='row gy-3 text-lg'>
                {/* Item Total */}
                {/* <div className='col-md-4'>
                  <label className='fw-semibold font-20'>Invoice Date</label>
                  <div className='font-20'>{invoiceSummary.invoiceDate}</div>
                </div> */}
                <div className='col-md-4'>
                  <label className='fw-semibold font-20'>Item Total</label>
                  <div className='font-20'>
                    ฿ {invoiceSummary.itemTotal.toFixed(2)}
                  </div>
                </div>
                {/* Discount */}
                <div className='col-md-4'>
                  <label className='fw-semibold font-20'>Discount</label>
                  <div className='font-20'>
                    ฿ {invoiceSummary.discount.toFixed(2)}
                  </div>
                </div>
                {/* Sub Total */}
                <div className='col-md-4'>
                  <label className='fw-semibold font-20'>Sub Total</label>
                  <div className='font-20'>
                    ฿ {invoiceSummary.subTotal.toFixed(2)}
                  </div>
                </div>
                 {/* Tax Summary: Moved to separate row for clarity */}
                <div className='col-md-4'>
                  <label className='fw-semibold font-20'>Tax</label>
                  <div className='font-20'>
                    {invoiceSummary.taxPercent}: ฿ {invoiceSummary.tax.toFixed(2)}
                  </div>
                </div>
                {/* Round Off */}
                <div className='col-md-4'>
                  <label className='fw-semibold font-20'>Round Off</label>
                  <div className='font-20'>
                    ฿ {invoiceSummary.roundOff.toFixed(2)}
                  </div>
                </div>
                {/* Grand Total */}
                <div className='col-md-4'>
                  <label className='fw-bold font-20'>Grand Total</label>
                  <div className='text-success fw-bold font-20'>
                    ฿ {invoiceSummary.grandTotal.toFixed(2)}
                  </div>
                </div>
               
                
              </div>
            </CardComponent>
          </div>
        </div>

        {/* Action Buttons Row */}
        <div className='row mb-4'>
          <div className='col-md-12'>
            <CardComponent>
              <div className='d-flex flex-wrap gap-2 justify-content-end'>
                <button
                  className='btn btn-success'
                  onClick={async () => {
                    // Validate
                    if (!customer.name || !customer.phone) {
                      toast.error('Please enter customer name and phone.');
                      return;
                    }
                    if (itemRows.length === 0) {
                      toast.error('Please add at least one item.');
                      return;
                    }
                    try {
                       const setupDate = await getNextSetupDate();
                      // Prepare bill data
                      const billData = {
                        customer_id: customer.custid || null,
                        customer_name: customer.name || 'Walk-in Customer',
                        tablenumber: customer.deliveryPlace || '',
                        subtotal: invoiceSummary.itemTotal || 0,
                        discount_type: discountType || 'amount',
                        discount_value: discountValue || 0,
                        subtotal_afterdiscount: invoiceSummary.subTotal || 0,
                        tax: invoiceSummary.tax || 0,
                        round_off: invoiceSummary.roundOff || 0,
                        grand_total: invoiceSummary.grandTotal || 0,
                        payment_mode: paymentType,
                        status: 'Pending',
                        setup_date: setupDate,
                        bank_account_id: bankAccountId,
                        reference_number: referenceNumber,
                        remarks: remarks,
                        order_items: itemRows.map(row => ({
                          item_name: row.itemName,
                          quantity: row.quantity,
                          rate: row.rate,
                          total_price: row.netAmount
                        }))
                      };
                      // Save bill and get bill_id
                      const res = await axios.post(
                        '/savebill',
                        billData,
                        getHeaders()
                      );
                      const bill_id = res.data.bill_id;
                      window.lastSavedBillId = bill_id;
                      // Prepare order items
                     
                      const orderItems = itemRows.map(row => {
                         console.log('Saving bill with ID:', row.amount);
                        if (taxType === 'gst') {
                          return {
                            order_id: bill_id,
                            invoice_number: bill_id,
                            table_number: customer.deliveryPlace || '',
                            item_name: row.itemName,
                            quantity: row.quantity,
                            uom: row.uom || '',
                            rate: row.rate,
                            cgst: row.cgst || 0,
                            sgst: row.sgst || 0,
                            igst: row.igst || 0,
                            tax_amount:
  (row.amount - row.discountValue) *
  ((row.cgst + row.sgst + row.igst) / 100),

                            total_price: row.amount,
                            status: '1',
                            setup_date: setupDate // ✅ Add setup_date column
                          };
                        } else {
                          return {
                            order_id: bill_id,
                            invoice_number: bill_id,
                            table_number: customer.deliveryPlace || '',
                            item_name: row.itemName,
                            description: row.description || '',
                            quantity: row.quantity,
                            rate: row.rate || 0,
                            discount_percent: row.discountPercent || 0,
                            vat: row.vat || 7,
                            total_price: row.netAmount,
                            status: '1',
                            setup_date: setupDate // ✅ Add setup_date column
                          };
                        }
                      });
                      //console.log('Order Items:', orderItems);
                      // Insert into correct table
                      if (taxType === 'gst') {
                        await axios.post(
                          '/insertdatabulkgst/order_items_gst',
                          { items: orderItems },
                          getHeaders()
                        );
                      } else {
                        await axios.post(
                          '/insertdatabulk/order_items',
                          { items: orderItems },
                          getHeaders()
                        );
                      }
                      toast.success(
                        `Invoice generated successfully. Invoice No: ${bill_id}`
                      );
                      
                      // Store last saved invoice data for printing
                      setLastSavedInvoice({
                        invoice_number: bill_id,
                        setup_date: setupDate,
                        payment_method: paymentType,
                        customer_name: customer.name || 'Walk-in Customer',
                        customer_phone: customer.phone || 'N/A',
                        customer_email: customer.email || '',
                        customer_address: customer.address || '',
                        customer_gst: customer.gst || '',
                        items: itemRows.map(row => ({
                          item_name: row.itemName,
                          rate: row.rate,
                          quantity: row.quantity,
                          discount_percent: row.discountPercent,
                          vat: row.vat || 7,
                          total_amount: row.netAmount,
                          offerprice: row.rate
                        })),
                        subtotal: invoiceSummary.itemTotal,
                        discount_value: discountValue,
                        tax: invoiceSummary.tax,
                        round_off: invoiceSummary.roundOff,
                        grand_total: invoiceSummary.grandTotal,
                        total: invoiceSummary.itemTotal
                      });
                      
                      // Reset all data for next bill
                      setItemRows([]);
                      setCustomer({
                        name: '',
                        phone: '',
                        email: '',
                        address: '',
                        gst: '',
                        deliveryPlace: ''
                      });
                      setDiscountValue(0);
                      setPaymentType('Cash');
                      setDiscountType('percentage');
                      setBankAccountId(null);
                      setReferenceNumber('');
                      setRemarks('');
                      
                      // Clear customer display
                      if (isCustomerDisplayOpen) {
                        customerDisplayManager.clearCustomerDisplay();
                      }
                    } catch (err) {
                      toast.error('Error saving bill.');
                    }
                  }}
                  disabled={isEditMode}
                >
                  💾 Save Bill
                </button>

                <button
                  className='btn btn-success'
                  disabled={isEditMode}
                  onClick={async () => {
                    if (itemRows.length === 0) {
                      toast.error('Please add items first');
                      return;
                    }
                    try {
                       const setupDate = await getNextSetupDate();
                      // Prepare bill data
                      const billData = {
                        customer_id: customer.custid || null,
                        customer_name: customer.name || 'Walk-in Customer',
                        tablenumber: customer.deliveryPlace || '',
                        subtotal: invoiceSummary.itemTotal || 0,
                        discount_type: discountType || 'amount',
                        discount_value: discountValue || 0,
                        subtotal_afterdiscount: invoiceSummary.subTotal || 0,
                        tax: invoiceSummary.tax || 0,
                        round_off: invoiceSummary.roundOff || 0,
                        grand_total: invoiceSummary.grandTotal || 0,
                        payment_mode: paymentType,
                        status: 'Pending',
                        setup_date: setupDate,
                        bank_account_id: bankAccountId,
                        reference_number: referenceNumber,
                        remarks: remarks,
                        order_items: itemRows.map(row => ({
                          item_name: row.itemName,
                          quantity: row.quantity,
                          rate: row.rate,
                          total_price: row.netAmount
                        }))
                      };
                      // Save bill and get bill_id
                      const res = await axios.post(
                        '/savebill',
                        billData,
                        getHeaders()
                      );
                      const bill_id = res.data.bill_id;
                      
                      // Prepare order items
                      const orderItems = itemRows.map(row => {
                        if (taxType === 'gst') {
                          return {
                            order_id: bill_id,
                            invoice_number: bill_id,
                            table_number: customer.deliveryPlace || '',
                            item_name: row.itemName,
                            description: row.description || '',
                            quantity: row.quantity,
                            uom: row.uom || '',
                            rate: row.rate,
                            cgst: row.cgst || 0,
                            sgst: row.sgst || 0,
                            igst: row.igst || 0,
                            tax_amount:
  (row.amount - row.discountValue) *
  ((row.cgst + row.sgst + row.igst) / 100),
                            total_price: row.amount,
                            status: '1',
                            setup_date: setupDate
                          };
                        } else {
                          return {
                            order_id: bill_id,
                            invoice_number: bill_id,
                            table_number: customer.deliveryPlace || '',
                            item_name: row.itemName,
                            description: row.description || '',
                            quantity: row.quantity,
                            rate: row.rate || 0,
                            discount_percent: row.discountPercent || 0,
                            vat: row.vat || 7,
                            total_price: row.netAmount,
                            status: '1',
                            setup_date: setupDate
                          };
                        }
                      });
                      
                      // Insert into correct table
                      if (taxType === 'gst') {
                        await axios.post(
                          '/insertdatabulkgst/order_items_gst',
                          { items: orderItems },
                          getHeaders()
                        );
                      } else {
                        await axios.post(
                          '/insertdatabulk/order_items',
                          { items: orderItems },
                          getHeaders()
                        );
                      }
                      
                      toast.success(
                        `Invoice generated successfully. Invoice No: ${bill_id}`
                      );
                      
                      // Store last saved invoice data for printing
                      const newInvoiceData = {
                        invoice_number: bill_id,
                        setup_date: setupDate,
                        payment_method: paymentType,
                        customer_name: customer.name || 'Walk-in Customer',
                        customer_phone: customer.phone || 'N/A',
                        customer_email: customer.email || '',
                        customer_address: customer.address || '',
                        customer_gst: customer.gst || '',
                        items: itemRows.map(row => ({
                          item_name: row.itemName,
                          description: row.description || '',
                          rate: row.rate,
                          quantity: row.quantity,
                          discount_percent: row.discountPercent,
                          vat: row.vat || 7,
                          total_amount: row.netAmount,
                          offerprice: row.rate,
                          iname: row.itemName
                        })),
                        subtotal: invoiceSummary.itemTotal,
                        discount_value: discountValue,
                        tax: invoiceSummary.tax,
                        round_off: invoiceSummary.roundOff,
                        grand_total: invoiceSummary.grandTotal,
                        total: invoiceSummary.itemTotal
                      };
                      
                      setLastSavedInvoice(newInvoiceData);
                      window.lastSavedBillId = bill_id;
                      
                      // Print VAT Format immediately using state data (not reprint from DB)
                      setTimeout(() => {
                        printBillVATFormat();
                      }, 500);
                      
                      // Reset all data for next bill
                      setItemRows([]);
                      setCustomer({
                        name: '',
                        phone: '',
                        email: '',
                        address: '',
                        gst: '',
                        deliveryPlace: ''
                      });
                      setDiscountValue(0);
                      setPaymentType('Cash');
                      setDiscountType('percentage');
                      setBankAccountId(null);
                      setReferenceNumber('');
                      setRemarks('');
                      
                      // Clear customer display
                      if (isCustomerDisplayOpen) {
                        customerDisplayManager.clearCustomerDisplay();
                      }
                    } catch (err) {
                      toast.error('Error saving bill.');
                      console.error('Save and print error:', err);
                    }
                  }}
                  title='Save bill and print VAT format invoice'
                >
                  💾📄 Save & Print Bill
                </button>

                <button
                  className='btn btn-danger'
                  onClick={() => {
                    setItemRows([])
                    setCustomer({
                      name: '',
                      phone: '',
                      email: '',
                      address: '',
                      gst: '',
                      deliveryPlace: ''
                    })
                    setDiscountValue(0);
                    setPaymentType('Cash');
                    setDiscountType('percentage');
                    setBankAccountId(null);
                    setReferenceNumber('');
                    setRemarks('');
                    // Clear customer display when cancelling bill
                    if (isCustomerDisplayOpen) {
                      customerDisplayManager.clearCustomerDisplay();
                    }
                  }}
                >
                  ❌ Cancel Bill
                </button>

                <button 
                  className={`btn ${isCustomerDisplayOpen ? 'btn-success' : 'btn-info'}`}
                  onClick={toggleCustomerDisplay}
                  title={isCustomerDisplayOpen ? 'Close Customer Display' : 'Open Customer Display'}
                >
                  {isCustomerDisplayOpen ? '📺 Close Display' : '📺 Customer Display'}
                </button>

                <button className='btn btn-warning text-dark'>
                  🔄 Reset Bill
                </button>

                <button
                  className='btn btn-primary'
                  onClick={async () => {
                    let latestBillId = window.lastSavedBillId;
                    if (!latestBillId) {
                      toast.error('No bill found to print. Please save a bill first.');
                      return;
                    }
                    // Fetch invoice props only when print is requested
                    const props = await getInvoiceProps(latestBillId);
                    if (taxType === 'gst') {
                      setGstInvoiceProps(props);
                      setShowInvoicePreview(true);
                    } else {
                      setVatInvoiceProps(props);
                      setShowVATInvoicePreview(true);
                    }
                  }}
                >
                  🖨️ Print Bill
                </button>

                <button
                  onClick={handleBillHistory}
                  className='btn btn-purple text-white'
                  style={{ backgroundColor: '#6f42c1' }}
                >
                  📜 Invoice History
                </button>
              </div>

              {/* Reprint & Edit Invoice Section - Two Columns */}
              <div className='row mt-3'>
                {/* Reprint Invoice Column */}
                <div className='col-md-6'>
                  <div className='p-3 h-100' style={{ border: '2px solid #FF8A80', borderRadius: '8px', backgroundColor: '#FFF5F5' }}>
                    <h6 style={{ color: '#FF8A80', marginBottom: '10px', fontWeight: 'bold' }}>
                      🖨️ Reprint Invoice
                    </h6>
                    <div className='d-flex gap-2 align-items-center'>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Enter Invoice Number'
                        value={reprintInvoiceNo}
                        onChange={(e) => setReprintInvoiceNo(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            if (!reprintInvoiceNo.trim()) {
                              toast.error('Please enter invoice number');
                              return;
                            }
                            printBillVATFormat(reprintInvoiceNo.trim());
                          }
                        }}
                        style={{ flex: 1, borderColor: '#FF8A80' }}
                      />
                      <button
                        className='btn btn-danger'
                        onClick={() => {
                          if (!reprintInvoiceNo.trim()) {
                            toast.error('Please enter invoice number');
                            return;
                          }
                          printBillVATFormat(reprintInvoiceNo.trim());
                        }}
                        style={{ 
                          backgroundColor: '#FF8A80', 
                          borderColor: '#FF8A80',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        🖨️ Print
                      </button>
                    </div>
                    <small className='text-muted d-block mt-2'>
                      Print merchant and customer copies
                    </small>
                  </div>
                </div>

                {/* Edit Invoice Column */}
                <div className='col-md-6'>
                  <div className='p-3 h-100' style={{ 
                    border: isEditMode ? '2px solid #10b981' : '2px solid #3b82f6', 
                    borderRadius: '8px', 
                    backgroundColor: isEditMode ? '#f0fdf4' : '#eff6ff' 
                  }}>
                <h6 style={{ 
                  color: isEditMode ? '#10b981' : '#3b82f6', 
                  marginBottom: '10px', 
                  fontWeight: 'bold' 
                }}>
                  {isEditMode ? '✏️ Editing Invoice' : '✏️ Edit Invoice'}
                </h6>
                
                {!isEditMode ? (
                  <>
                    <div className='d-flex gap-2 align-items-center'>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Enter Invoice Number to Edit'
                        value={editInvoiceNo}
                        onChange={(e) => setEditInvoiceNo(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            promptEditPassword(editInvoiceNo);
                          }
                        }}
                        style={{ flex: 1, borderColor: '#3b82f6' }}
                      />
                      <button
                        className='btn btn-primary'
                        onClick={() => promptEditPassword(editInvoiceNo)}
                        style={{ 
                          backgroundColor: '#3b82f6', 
                          borderColor: '#3b82f6',
                          fontWeight: 'bold',
                          minWidth: '150px'
                        }}
                      >
                        📝 Load for Edit
                      </button>
                    </div>
                    <small className='text-muted d-block mt-2'>
                      Enter invoice number to load bill for editing. Changes will update ledger entries automatically.
                    </small>
                  </>
                ) : (
                  <>
                    <div className='alert alert-success mb-3' style={{ 
                      backgroundColor: '#d1fae5', 
                      borderColor: '#10b981',
                      color: '#047857'
                    }}>
                      <strong>📝 Editing Bill #{editBillId}</strong>
                      <p className='mb-0 mt-1 small'>Make changes to items, customer, or payment details below and click Save Changes.</p>
                    </div>
                    <div className='d-flex gap-2'>
                      <button
                        className='btn btn-success flex-fill'
                        onClick={saveEditedBill}
                        style={{ 
                          backgroundColor: '#10b981', 
                          borderColor: '#10b981',
                          fontWeight: 'bold'
                        }}
                      >
                        💾 Save Changes
                      </button>
                      <button
                        className='btn btn-warning flex-fill'
                        onClick={cancelEditMode}
                        style={{ 
                          fontWeight: 'bold'
                        }}
                      >
                        ❌ Cancel Edit
                      </button>
                    </div>
                  </>
                )}
                  </div>
                </div>
              </div>
            </CardComponent>
          </div>
        </div>

        {/* Print Preview Modal */}
       <GSTInvoicePrintPreview
  open={showInvoicePreview}
  onClose={() => setShowInvoicePreview(false)}
  {...(gstInvoiceProps || {})}
/>
        <VATInvoicePrintPreview
          open={showVATInvoicePreview}
          onClose={() => setShowVATInvoicePreview(false)}
          {...(vatInvoiceProps || {})}
        />
        {/* <InvoiceTableModal
          show={showModal}
          onClose={() => setShowModal(false)}
          companyDetails={companyDetails}
          customerDetails={customerDetails1}
          data={invoiceData}
        /> */}

        {/* Password Modal for Edit Bill */}
        {showPasswordModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: '12px', border: '2px solid #3b82f6' }}>
                <div className="modal-header" style={{ 
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  borderTopLeftRadius: '10px',
                  borderTopRightRadius: '10px'
                }}>
                  <h5 className="modal-title">
                    <i className="fas fa-lock me-2"></i>
                    Password Required
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setEditPassword('');
                    }}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <div className="alert alert-warning" style={{ backgroundColor: '#f53333ff', borderColor: '#ffc107' }}>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Authorization Required</strong>
                    <p className="mb-0 mt-1 small">Enter password to edit Bill #{pendingEditInvoiceNo}</p>
                  </div>
                  <div className="form-group">
                    <label className="form-label fw-semibold">Password</label>
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Enter password"
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          verifyPasswordAndLoadBill();
                        }
                      }}
                      autoFocus
                      style={{ 
                        borderRadius: '8px',
                        borderColor: '#3b82f6',
                        padding: '10px 15px',
                        fontSize: '1rem'
                      }}
                    />
                    <small className="text-muted mt-1 d-block">
                      Please contact administrator if you don't have access
                    </small>
                  </div>
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setEditPassword('');
                    }}
                    style={{ borderRadius: '8px', fontWeight: '500' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={verifyPasswordAndLoadBill}
                    style={{ 
                      backgroundColor: '#3b82f6',
                      borderColor: '#3b82f6',
                      borderRadius: '8px',
                      fontWeight: '500'
                    }}
                  >
                    <i className="fas fa-unlock me-2"></i>
                    Verify & Load
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add New Customer Modal */}
        <AddCustomerModal
          show={showAddCustomerModal}
          onClose={() => setShowAddCustomerModal(false)}
          onSubmit={handleAddCustomer}
          newCustomer={newCustomer}
          handleInput={handleNewCustomerInput}
        />
      </div>
    </>
  );

  // Return without Layout wrapper, with custom top navigation
  return (
    <div className="pos-page" style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Enhanced Professional Top Navigation */}
      <nav 
        className="navbar navbar-expand-lg navbar-dark" 
        style={{ 
          background: 'linear-gradient(135deg, #d6720dff 0%, #c55c07ff 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          marginBottom: '30px',
          borderBottom: '3px solid #df5f0aff'
        }}
      >
        <div className="container-fluid d-flex align-items-center justify-content-between py-2">
          <span className="navbar-brand mb-0 h1 d-flex align-items-center" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
            <i className="fas fa-cash-register me-3" style={{ fontSize: '1.8rem', color: '#f1eeecff' }}></i>
            <span style={{ letterSpacing: '0.5px' }}>StoreHub</span>
          </span>
          
          <div className="d-flex align-items-center gap-3">
            <button 
              className="btn btn-outline-light me-2"
              onClick={navigateToDashboard}
              title={`Go to ${userType === 'Admin' ? 'Analytics' : userType === 'Cashier' ? 'Cashier' : ''} Dashboard`}
              style={{ 
                fontWeight: '500',
                borderRadius: '25px',
                padding: '8px 20px',
                border: '2px solid rgba(255,255,255,0.8)',
                background: 'rgba(255,255,255,0.1)',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.2)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.1)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <i className="fas fa-tachometer-alt me-2"></i>
              {userType === 'Admin' ? 'Analytics Dashboard' : userType === 'Cashier' ? 'Cashier Dashboard' : 'Dashboard'}
            </button>
            
            <button 
              className="btn btn-outline-danger"
              onClick={handleLogout}
              title="Logout"
              style={{ 
                fontWeight: '500',
                borderRadius: '25px',
                padding: '8px 20px',
                border: '2px solid #fcf9f9ff',
                background: 'rgba(255,107,107,0.1)',
                color: '#f8f4f4ff',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#ff6b6b';
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255,107,107,0.1)';
                e.target.style.color = '#ff6b6b';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <i className="fas fa-sign-out-alt me-2"></i>
              Logout
            </button>
          </div>
        </div>
      </nav>
      
      {/* Professional Page Content Container */}
      <div className="container-fluid px-4" style={{ width: '100%' }}>
        {renderContent()}
      </div>
    </div>
  );
}
